import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { analyticsAPI, proxyImageUrl, cheeseAPI } from '../api/client';
import { useAuthStore } from '../store';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt  = (n) => n == null ? '—' : Number(n).toLocaleString('ru-RU');
const pct  = (n) => n == null ? '—' : `${n}%`;
const COLORS = ['var(--lg-accent)', 'var(--lg-red)', '#4ecdc4', '#ffe66d', '#a78bfa', '#f97316'];

function ProductCell({ photo, art, name, onClick }) {
  const isAdmin = useAuthStore(s => s.user?.is_admin);
  const [added, setAdded] = useState(false);
  const [pending, setPending] = useState(false);
  const [hasBox, setHasBox] = useState(false);

  const handleConfirm = async (e) => {
    e.stopPropagation();
    try {
      await cheeseAPI.createOrder({ name, photo_url: photo || null, catalog_sku: art || null, sizes_qty: {}, has_box: hasBox });
      setPending(false);
      setHasBox(false);
      setAdded(true);
      setTimeout(() => setAdded(false), 2500);
    } catch {
      alert('Ошибка добавления в закупку');
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setPending(false);
    setHasBox(false);
  };

  return (
    <div className={`an-product-row${onClick ? ' an-product-row--clickable' : ''}`} onClick={onClick}>
      {photo
        ? <img className="an-thumb" src={proxyImageUrl(photo)} alt="" loading="lazy" />
        : <div className="an-thumb an-thumb--empty" />
      }
      <div className="an-product-info">
        <span className="an-art-cell">{art}</span>
        <span className="an-name-cell">{name}</span>
      </div>
      {isAdmin && !pending && (
        <button
          className="mng-btn-secondary cheese-btn-sm"
          style={{ marginLeft: 6, color: added ? 'var(--lg-green)' : undefined, flexShrink: 0 }}
          onClick={e => { e.stopPropagation(); if (!added) setPending(true); }}
          title="В закупку"
        >
          {added ? '✓' : '+'}
        </button>
      )}
      {isAdmin && pending && (
        <div className="an-order-quick" onClick={e => e.stopPropagation()}>
          <label className="an-order-box-check">
            <input type="checkbox" checked={hasBox} onChange={e => setHasBox(e.target.checked)} />
            BOX
          </label>
          <button className="an-order-quick-confirm" onClick={handleConfirm} title="Добавить в закупку">✓</button>
          <button className="an-order-quick-cancel" onClick={handleCancel} title="Отмена">✕</button>
        </div>
      )}
      {onClick && <span className="an-product-detail-hint">→</span>}
    </div>
  );
}

const TOOLTIP = {
  contentStyle: { background: 'var(--lg-bg-sidebar)', border: '1px solid #333', borderRadius: 8, color: 'var(--lg-text)', fontSize: 13 },
  labelStyle: { color: 'var(--lg-text-3)' },
};

// ---------------------------------------------------------------------------
// Frontend cache + stale-while-revalidate
// ---------------------------------------------------------------------------

function useCached(fetcher, key) {
  const cache = useRef({});
  const [data,  setData]  = useState(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;

    const cached = cache.current[key];
    if (cached) { setData(cached); setStale(true); setError(false); }
    else         { setStale(false); }

    fetcher()
      .then(r  => { if (!cancelled) { cache.current[key] = r.data; setData(r.data); setStale(false); setError(false); } })
      .catch(() => { if (!cancelled) { setStale(false); if (!cached) setError(true); } });

    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line

  return { data, loading: !data && !error, stale, error };
}

// ---------------------------------------------------------------------------
// UI atoms
// ---------------------------------------------------------------------------

function PeriodPicker({ value, onChange, options }) {
  return (
    <div className="an-period-picker">
      {options.map(o => (
        <button key={o.value} className={`an-period-btn${value === o.value ? ' active' : ''}`}
          onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

function Section({ title, children, action, stale }) {
  return (
    <div className="an-section">
      <div className="an-section-head">
        <span className="an-section-title">
          {title}{stale && <span className="an-stale-dot" title="Обновление…" />}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

// Бейдж статуса Cheese-закупки/пути
function CheeseStatusBadge({ statuses }) {
  if (!statuses || statuses.length === 0) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {statuses.includes('order') && (
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
          background: '#f97316', color: '#fff', whiteSpace: 'nowrap',
        }}>В заказах</span>
      )}
      {statuses.includes('transit') && (
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
          background: 'var(--lg-accent)', color: '#fff', whiteSpace: 'nowrap',
        }}>В пути</span>
      )}
    </span>
  );
}

// Хук для загрузки Cheese-статусов по списку SKU
function useCheeseStatus(skus) {
  const [status, setStatus] = useState({});
  useEffect(() => {
    if (!skus || skus.length === 0) return;
    const unique = [...new Set(skus.filter(Boolean))];
    if (unique.length === 0) return;
    analyticsAPI.getCheeseStatus(unique)
      .then(r => setStatus(r.data || {}))
      .catch(() => {});
  }, [skus.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  return status;
}

function Loader() {
  return <div className="an-loading"><div className="an-spinner" /></div>;
}

function ErrBox() {
  return <div className="an-error">Ошибка загрузки данных</div>;
}

const PERIOD_OPTS = [
  { value: 7,   label: '7 дней' },
  { value: 30,  label: '30 дней' },
  { value: 90,  label: '90 дней' },
  { value: 180, label: '6 мес' },
  { value: 365, label: '1 год' },
];

const GROUP_OPTS = [
  { value: 'day',   label: 'По дням' },
  { value: 'week',  label: 'По неделям' },
  { value: 'month', label: 'По месяцам' },
];

// ---------------------------------------------------------------------------
// Forecast modal
// ---------------------------------------------------------------------------

function ForecastModal({ art, onClose }) {
  const { data, loading, error } = useCached(
    () => analyticsAPI.getForecast(art, { days: 30 }),
    `forecast:${art}`,
  );

  const combined = data
    ? [...data.history.slice(-30).map(d => ({ day: d.day, fact: d.sales })),
       ...data.forecast.map(d => ({ day: d.day, prognoz: d.sales }))]
    : [];

  return (
    <div className="an-modal-overlay" onClick={onClose}>
      <div className="an-modal" onClick={e => e.stopPropagation()}>
        <div className="an-modal-head">
          <span>{data?.name || art}</span>
          <button className="an-modal-close" onClick={onClose}>✕</button>
        </div>
        {loading && <Loader />}
        {error && <ErrBox />}
        {data && (
          <>
            <div className="an-modal-meta">
              Среднее в день: <b>{data.avg_daily}</b> шт &nbsp;·&nbsp; прогноз 30 дней (скользящее среднее 7д)
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={combined}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#666' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12, fill: '#666' }} />
                <Tooltip {...TOOLTIP} />
                <Legend wrapperStyle={{ color: 'var(--lg-text-3)', fontSize: 13 }} />
                <Line dataKey="fact"    stroke="#4f8cff" dot={false} name="Факт"    strokeWidth={2} />
                <Line dataKey="prognoz" stroke="#a78bfa" dot={false} name="Прогноз" strokeWidth={2} strokeDasharray="6 3" />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product report modal — world-class per-product analytics
// ---------------------------------------------------------------------------

function KpiTile({ label, value, sub, color = 'blue' }) {
  return (
    <div className={`an-kpi-tile an-kpi-tile--${color}`}>
      <div className="an-kpi-tile-label">{label}</div>
      <div className="an-kpi-tile-value">{value}</div>
      {sub != null && <div className="an-kpi-tile-sub">{sub}</div>}
    </div>
  );
}

const MONTH_SHORT = ['Янв','Фев','Мар','Апр','Май','Июн','Июл','Авг','Сен','Окт','Ноя','Дек'];

function ReportSalesTab({ data }) {
  const k = data.kpi;

  const insights = [];
  if (k.days_left !== 999 && k.days_left <= 7)
    insights.push({ type: 'critical', text: `Сток заканчивается через ${k.days_left} дн. — срочно пополнить!` });
  else if (k.days_left !== 999 && k.days_left <= 14)
    insights.push({ type: 'warn', text: `Осталось ${k.days_left} дней — планировать закупку` });
  if (k.trend_pct >= 30)
    insights.push({ type: 'good', text: `Продажи выросли на +${k.trend_pct}% — горячий спрос` });
  else if (k.trend_pct <= -30)
    insights.push({ type: 'warn', text: `Продажи упали на ${Math.abs(k.trend_pct)}% за неделю` });
  if (k.return_rate_30d > 30)
    insights.push({ type: 'warn', text: `${k.return_rate_30d}% возвратов за 30 дн. — разобраться с причинами` });
  if (data.active_days_30 >= 25)
    insights.push({ type: 'good', text: `Стабильный товар — активен ${data.active_days_30} из 30 дней` });
  if (k.is_anomaly)
    insights.push({ type: 'warn', text: `Аномальный скачок продаж — возможна акция или снижение цены поставщиком. Данные скорректированы по среднему за 30д. Не ориентируйтесь на пиковые цифры при закупке.` });
  else if (k.trend_pct >= 60 && (k.sales_7d || 0) >= 5)
    insights.push({ type: 'warn', text: `Продажи выросли на +${k.trend_pct}% — проверьте, не снизил ли поставщик цену. Закупайте по обычному темпу до выяснения причины роста.` });
  if (k.sales_all > 0 && k.sales_30d === 0)
    insights.push({ type: 'warn', text: `Нет продаж за последние 30 дней` });

  const maxSales = Math.max(...(data.sizes || []).map(s => s.sales), 1);
  const maxStock = Math.max(...(data.sizes || []).map(s => s.stock_total), 1);

  return (
    <div className="an-report-sales-tab">
      {insights.length > 0 && (
        <div className="an-insights">
          {insights.map((ins, i) => (
            <div key={i} className={`an-insight an-insight--${ins.type}`}>
              {ins.type === 'critical' ? '❗' : ins.type === 'good' ? '✅' : ins.type === 'warn' ? '⚠️' : 'ℹ️'} {ins.text}
            </div>
          ))}
        </div>
      )}

      <div className="an-report-section-title">Продажи и возвраты — последние 90 дней</div>
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data.daily_chart} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="rg-sales" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#4f8cff" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#4f8cff" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="rg-ret" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#ff6b6b" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#ff6b6b" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
          <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#555' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11, fill: '#555' }} />
          <Tooltip {...TOOLTIP} />
          <Legend wrapperStyle={{ color: 'var(--lg-text-3)', fontSize: 12 }} />
          <Area type="monotone" dataKey="sales"   stroke="#4f8cff" fill="url(#rg-sales)" name="Продажи"  strokeWidth={2} dot={false} />
          <Area type="monotone" dataKey="returns" stroke="#ff6b6b" fill="url(#rg-ret)"   name="Возвраты" strokeWidth={1.5} dot={false} />
        </AreaChart>
      </ResponsiveContainer>

      {data.sizes.length > 0 && (
        <>
          <div className="an-report-section-title" style={{ marginTop: 24 }}>Размерный профиль</div>
          <div className="an-size-profile">
            {data.sizes.map(s => (
              <div key={s.size} className="an-size-profile-item">
                <div className="an-size-profile-bars">
                  <div
                    className="an-size-profile-bar an-size-profile-bar--sales"
                    style={{ height: `${Math.round(s.sales / maxSales * 52)}px` }}
                    title={`Продажи: ${s.sales} шт (${s.pct}%)`}
                  />
                  <div
                    className="an-size-profile-bar an-size-profile-bar--stock"
                    style={{ height: `${Math.round(s.stock_total / maxStock * 52)}px` }}
                    title={`Сток: ${s.stock_total} шт`}
                  />
                </div>
                <div className="an-size-profile-sz">{s.size}</div>
                <div className="an-size-profile-pct">{s.pct > 0 ? `${s.pct}%` : '—'}</div>
                <div className="an-size-profile-stk" title="Текущий сток">
                  {s.stock_total > 0 ? `${s.stock_total}` : <span style={{ color: 'var(--lg-text-3)' }}>0</span>}
                </div>
              </div>
            ))}
            <div className="an-size-profile-legend">
              <span><span className="an-size-legend-dot" style={{ background: 'var(--lg-accent)' }} />Продажи</span>
              <span><span className="an-size-legend-dot" style={{ background: '#4ecdc4' }} />Сток</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ReportStockTab({ data }) {
  const activeSizes = _getAllActiveSizes(data);

  return (
    <div className="an-report-stock-tab">
      <div className="an-report-section-title">Наличие по поставщикам</div>
      <div className="an-stock-table-wrap">
        <table className="an-table an-stock-table">
          <thead>
            <tr>
              <th>Поставщик</th>
              <th>Цена</th>
              <th>Всего</th>
              {activeSizes.map(s => <th key={s} className="an-size-th">{s}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.suppliers.filter(s => s.stock > 0).map(sup => (
              <tr key={sup.supplier}>
                <td className="an-name-cell">{sup.supplier}</td>
                <td className="an-num">{sup.price > 0 ? `${fmt(sup.price)} грн` : '—'}</td>
                <td className="an-num an-stock-total"><b>{fmt(sup.stock)}</b></td>
                {activeSizes.map(s => {
                  const qty = sup.sizes[String(s)] || 0;
                  return (
                    <td key={s} className={`an-size-cell${qty === 0 ? ' an-size-cell--empty' : ''}`}>
                      {qty > 0 ? qty : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="an-stock-total-row">
              <td colSpan={2}><b>Итого</b></td>
              <td className="an-num"><b>{fmt(data.kpi.total_stock)}</b></td>
              {activeSizes.map(s => {
                const qty = parseInt(data.size_totals[String(s)] || 0);
                return (
                  <td key={s} className={`an-size-cell${qty === 0 ? ' an-size-cell--empty' : ' an-size-cell--total'}`}>
                    {qty > 0 ? <b>{qty}</b> : '—'}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function ReportSuppliersTab({ data }) {
  return (
    <div className="an-report-suppliers-tab">
      <div className="an-report-section-title">Продажи и возвраты по поставщикам (30 дней)</div>
      {data.supplier_perf.length === 0
        ? <div style={{ color: 'var(--lg-text-2)', padding: '20px 0' }}>Нет данных о продажах за 30 дней</div>
        : (
          <div className="an-table-wrap">
            <table className="an-table" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 36 }} />   {/* # */}
                <col />                          {/* Поставщик — занимает остаток */}
                <col style={{ width: 95 }} />   {/* Продажи 30д */}
                <col style={{ width: 90 }} />   {/* Активность */}
                <col style={{ width: 90 }} />   {/* Возвраты */}
                <col style={{ width: 80 }} />   {/* % возвр. */}
                <col style={{ width: 110 }} />  {/* Цена */}
                <col style={{ width: 80 }} />   {/* Сток */}
              </colgroup>
              <thead>
                <tr>
                  <th>#</th><th>Поставщик</th>
                  <th title="Продажи за 30 дней">Продажи 30д</th>
                  <th title="Активных дней продаж">Активность</th>
                  <th title="Возвраты за 30 дней">Возвраты</th>
                  <th title="% возвратов">% возвр.</th>
                  <th title="Цена у поставщика">Цена</th>
                  <th title="Текущий сток">Сток</th>
                </tr>
              </thead>
              <tbody>
                {data.supplier_perf.map((sp, i) => {
                  const stockInfo = data.suppliers.find(s => s.supplier === sp.supplier);
                  return (
                    <tr key={sp.supplier}>
                      <td className="an-rank">{i + 1}</td>
                      <td className="an-name-cell">{sp.supplier}</td>
                      <td className="an-num"><b>{fmt(sp.sales_30d)}</b></td>
                      <td className="an-num">{sp.active_days}д</td>
                      <td className="an-num">{fmt(sp.returns_30d)}</td>
                      <td className={`an-num an-pct${sp.return_rate > 30 ? ' an-bad' : sp.return_rate > 15 ? ' an-warn' : ' an-ok'}`}>
                        {pct(sp.return_rate)}
                      </td>
                      <td className="an-num">{stockInfo?.price > 0 ? `${fmt(stockInfo.price)} грн` : '—'}</td>
                      <td className="an-num">{fmt(stockInfo?.stock || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      }

      {data.kpi.price_min > 0 && data.kpi.price_max > data.kpi.price_min && (
        <div className="an-price-range-block">
          <div className="an-report-section-title" style={{ marginTop: 20 }}>Ценовой диапазон</div>
          <div className="an-price-range-row">
            <span className="an-price-label">Мин</span>
            <span className="an-price-val">{fmt(data.kpi.price_min)} грн</span>
            <div className="an-price-bar-wrap">
              <div className="an-price-bar" style={{ width: '100%' }}>
                <div className="an-price-bar-fill" style={{
                  marginLeft: 0,
                  width: `${((data.kpi.price_avg - data.kpi.price_min) / (data.kpi.price_max - data.kpi.price_min)) * 100}%`,
                }} />
              </div>
            </div>
            <span className="an-price-val">{fmt(data.kpi.price_max)} грн</span>
            <span className="an-price-label">Макс</span>
          </div>
          <div className="an-price-avg">Средняя цена: <b>{fmt(data.kpi.price_avg)} грн</b></div>
        </div>
      )}
    </div>
  );
}

function ReportSeasonalTab({ data }) {
  if (data.seasonal.length === 0) {
    return <div style={{ color: 'var(--lg-text-2)', padding: '20px 0' }}>Недостаточно данных для сезонного анализа</div>;
  }

  const years = [...new Set(data.seasonal.map(s => s.year))].sort();
  const chartData = MONTH_SHORT.map((mname, mi) => {
    const mo = mi + 1;
    const row = { month: mname };
    years.forEach(yr => {
      const found = data.seasonal.find(s => s.year === yr && s.month === mo);
      row[String(yr)] = found ? found.sales : 0;
    });
    return row;
  });

  const YEAR_COLORS = ['var(--lg-accent)', '#4ecdc4', '#ffe66d', '#a78bfa', '#f97316'];
  const maxMonth = data.seasonal.reduce((best, s) =>
    s.sales > (best?.sales || 0) ? s : best, null);

  return (
    <div className="an-report-seasonal-tab">
      {maxMonth && (
        <div className="an-seasonal-peak">
          Пиковый месяц: <b>{maxMonth.month_name} {maxMonth.year}</b> — {fmt(maxMonth.sales)} продаж
        </div>
      )}
      <div className="an-report-section-title">Продажи по месяцам</div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
          <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#888' }} />
          <YAxis tick={{ fontSize: 12, fill: '#555' }} />
          <Tooltip {...TOOLTIP} />
          <Legend wrapperStyle={{ color: 'var(--lg-text-3)', fontSize: 12 }} />
          {years.map((yr, i) => (
            <Bar key={yr} dataKey={String(yr)} name={String(yr)}
              fill={YEAR_COLORS[i % YEAR_COLORS.length]}
              radius={[3, 3, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function _getAllActiveSizes(data) {
  const set = new Set();
  (data.suppliers || []).forEach(sup => {
    Object.keys(sup.sizes || {}).forEach(s => set.add(parseInt(s)));
  });
  Object.keys(data.size_totals || {}).forEach(s => set.add(parseInt(s)));
  return [...set].sort((a, b) => a - b);
}

function ReportSizesTab({ data }) {
  const SALE_SIZES = [36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46];

  // Determine active sizes: any size with sales OR stock
  const sizeByPeriod = data.size_by_period || {};
  const sizeTotals   = data.size_totals    || {};

  const activeSizes = SALE_SIZES.filter(s => {
    const hasSales = (
      (sizeByPeriod.today  || {})[s] > 0 ||
      (sizeByPeriod.week   || {})[s] > 0 ||
      (sizeByPeriod.month  || {})[s] > 0 ||
      (sizeByPeriod.all    || {})[s] > 0
    );
    const hasStock = parseInt(sizeTotals[String(s)] || 0) > 0;
    return hasSales || hasStock;
  });

  // Compute totals row
  const totals = {
    today: 0, week: 0, month: 0, stock: 0,
  };
  activeSizes.forEach(s => {
    totals.today += (sizeByPeriod.today || {})[s] || 0;
    totals.week  += (sizeByPeriod.week  || {})[s] || 0;
    totals.month += (sizeByPeriod.month || {})[s] || 0;
    totals.stock += parseInt(sizeTotals[String(s)] || 0);
  });

  const activeSizesForStock = _getAllActiveSizes(data);

  return (
    <div className="an-report-sizes-tab">
      <div className="an-report-section-title">Аналитика по размерам</div>

      {activeSizes.length === 0 ? (
        <div style={{ color: 'var(--lg-text-2)', padding: '20px 0' }}>Нет данных по размерам</div>
      ) : (
        <div className="an-stock-table-wrap">
          <table className="an-size-full-table">
            <thead>
              <tr>
                <th>Размер</th>
                <th>Сегодня</th>
                <th>Неделя</th>
                <th>Месяц</th>
                <th>% мес.</th>
                <th>Сток</th>
                <th>% стока</th>
                <th>Возвр.%</th>
              </tr>
            </thead>
            <tbody>
              {activeSizes.map(s => {
                const today    = (sizeByPeriod.today || {})[s] || 0;
                const week     = (sizeByPeriod.week  || {})[s] || 0;
                const month    = (sizeByPeriod.month || {})[s] || 0;
                const stock    = parseInt(sizeTotals[String(s)] || 0);
                const monthPct = totals.month > 0 ? (month / totals.month * 100).toFixed(1) : '—';
                const stockPct = totals.stock > 0 ? (stock / totals.stock * 100).toFixed(1) : '—';
                const sz       = (data.sizes || []).find(x => x.size === s);
                const retRate  = sz?.return_rate ?? 0;

                // Row coloring: no stock but has sales = yellow; high return rate = red
                const noStock   = stock === 0 && month > 0;
                const highRet   = retRate > 30;
                const rowClass  = highRet ? 'an-size-no-stock' : noStock ? 'an-size-warn-row' : '';

                return (
                  <tr key={s} className={rowClass}>
                    <td>{s}</td>
                    <td>{today > 0 ? today : '—'}</td>
                    <td>{week  > 0 ? week  : '—'}</td>
                    <td>{month > 0 ? month : '—'}</td>
                    <td>{month > 0 ? `${monthPct}%` : '—'}</td>
                    <td>{stock > 0 ? stock : '—'}</td>
                    <td>{stock > 0 ? `${stockPct}%` : '—'}</td>
                    <td>{retRate > 0 ? <span style={{ color: highRet ? 'var(--lg-red)' : 'var(--lg-text-3)' }}>{retRate}%</span> : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="an-size-total-row">
                <td>Итого</td>
                <td>{totals.today > 0 ? totals.today : '—'}</td>
                <td>{totals.week  > 0 ? totals.week  : '—'}</td>
                <td>{totals.month > 0 ? totals.month : '—'}</td>
                <td>100%</td>
                <td>{totals.stock > 0 ? totals.stock : '—'}</td>
                <td>100%</td>
                <td>—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Per-supplier per-size stock sub-section */}
      {activeSizesForStock.length > 0 && (
        <>
          <div className="an-report-section-title" style={{ marginTop: 24 }}>Сток по поставщикам и размерам</div>
          <div className="an-stock-table-wrap">
            <table className="an-table an-stock-table">
              <thead>
                <tr>
                  <th>Поставщик</th>
                  <th>Всего</th>
                  {activeSizesForStock.map(s => <th key={s} className="an-size-th">{s}</th>)}
                </tr>
              </thead>
              <tbody>
                {(data.suppliers || []).filter(s => s.stock > 0).map(sup => (
                  <tr key={sup.supplier}>
                    <td className="an-name-cell">{sup.supplier}</td>
                    <td className="an-num an-stock-total"><b>{fmt(sup.stock)}</b></td>
                    {activeSizesForStock.map(s => {
                      const qty = sup.sizes[String(s)] || 0;
                      return (
                        <td key={s} className={`an-size-cell${qty === 0 ? ' an-size-cell--empty' : ''}`}>
                          {qty > 0 ? qty : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="an-stock-total-row">
                  <td><b>Итого</b></td>
                  <td className="an-num"><b>{fmt(data.kpi?.total_stock)}</b></td>
                  {activeSizesForStock.map(s => {
                    const qty = parseInt(sizeTotals[String(s)] || 0);
                    return (
                      <td key={s} className={`an-size-cell${qty === 0 ? ' an-size-cell--empty' : ' an-size-cell--total'}`}>
                        {qty > 0 ? <b>{qty}</b> : '—'}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const LOG_SIZES = [36,37,38,39,40,41,42,43,44,45,46];

function ReportSalesLogTab({ data, limit = 500 }) {
  const log = (data.sales_log || []).slice(0, limit);
  if (!log.length) return (
    <div style={{ color: 'var(--lg-text-3)', padding: '16px 0', fontSize: 14 }}>
      Нет данных о продажах за последние 90 дней
    </div>
  );

  const activeSizes = LOG_SIZES.filter(s => log.some(r => r[`SIZE${s}`] > 0));

  // Group by date, preserve order (log already sorted by date DESC)
  const byDate = [];
  const dateIndex = {};
  log.forEach(r => {
    const dateKey = String(r.DataTime || '').slice(0, 10);
    if (dateIndex[dateKey] === undefined) {
      dateIndex[dateKey] = byDate.length;
      byDate.push({ dateKey, rows: [], total: 0 });
    }
    const g = byDate[dateIndex[dateKey]];
    g.rows.push(r);
    g.total += (r.SIZEALL || 0);
  });

  return (
    <div className="an-table-wrap" style={{ maxHeight: 480, overflowY: 'auto' }}>
      <table className="an-table an-log-table">
        <thead>
          <tr>
            <th>Поставщик</th>
            <th style={{ textAlign: 'center' }}>Всего</th>
            <th>Артикул</th>
            {activeSizes.map(s => <th key={s} style={{ textAlign: 'center', minWidth: 38 }}>{s}</th>)}
          </tr>
        </thead>
        <tbody>
          {byDate.map(({ dateKey, rows, total }) => {
            const dateShort = dateKey.split('-').reverse().join('.');
            return (
              <React.Fragment key={dateKey}>
                <tr className="an-log-date-row">
                  <td colSpan={3 + activeSizes.length}>
                    <span className="an-log-date-label">{dateShort}</span>
                    <span className="an-log-date-total">{total} пар</span>
                  </td>
                </tr>
                {rows.map((r, i) => {
                  const isCheese = r.PostName?.toLowerCase().includes('cheese');
                  return (
                    <tr key={i} className={`an-log-row${isCheese ? ' an-log-row--cheese' : ''}`}>
                      <td className="an-log-art">
                        {isCheese
                          ? <span className="an-log-cheese-badge">{r.PostName}</span>
                          : r.PostName}
                      </td>
                      <td className="an-num" style={{ textAlign: 'center', fontWeight: 700 }}>{r.SIZEALL}</td>
                      <td style={{ fontSize: 11, color: 'var(--lg-text-3)' }}>{r.artPOST || '—'}</td>
                      {activeSizes.map(s => (
                        <td key={s} className="an-log-size-cell">
                          {r[`SIZE${s}`] > 0 ? r[`SIZE${s}`] : ''}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReportVerdictTab({ data, art }) {
  const [targetDays, setTargetDays] = useState(30);
  const [leadDays, setLeadDays] = useState(14);
  const [customQtyStr, setCustomQtyStr] = useState('');
  const [orderAdding,    setOrderAdding]    = useState(false);
  const [orderAdded,     setOrderAdded]     = useState(false);
  const [showOrderForm,  setShowOrderForm]  = useState(false);
  const [orderFormSizes, setOrderFormSizes] = useState({});
  const [orderHasBox,    setOrderHasBox]    = useState(false);
  const isAdmin = useAuthStore(s => s.user?.is_admin);
  const k = data.kpi;
  const sizes = data.sizes || [];
  const suppliers = data.suppliers || [];
  const supplierPerf = data.supplier_perf || [];

  // ── Health score ─────────────────────────────────────────────────
  let healthScore = 0;
  let healthLabel, healthCls;

  if (!k.sales_all || k.sales_30d === 0) {
    healthLabel = 'Нет активности'; healthCls = 'gray';
  } else {
    if (k.days_left >= 30) healthScore += 25;
    else if (k.days_left >= 15) healthScore += 15;
    else if (k.days_left >= 7) healthScore += 6;

    if (k.trend_pct >= 20) healthScore += 25;
    else if (k.trend_pct >= 0) healthScore += 18;
    else if (k.trend_pct >= -20) healthScore += 10;

    if (k.return_rate_30d < 10) healthScore += 25;
    else if (k.return_rate_30d < 20) healthScore += 16;
    else if (k.return_rate_30d < 30) healthScore += 8;

    if (data.active_days_30 >= 25) healthScore += 25;
    else if (data.active_days_30 >= 15) healthScore += 16;
    else if (data.active_days_30 >= 7) healthScore += 8;

    if (healthScore >= 80) { healthLabel = 'Топовый товар'; healthCls = 'green'; }
    else if (healthScore >= 60) { healthLabel = 'Стабильный'; healthCls = 'teal'; }
    else if (healthScore >= 35) { healthLabel = 'Под наблюдением'; healthCls = 'orange'; }
    else { healthLabel = 'Критично'; healthCls = 'red'; }
  }

  // ── Actions ───────────────────────────────────────────────────────
  const actions = [];

  const effectiveRateForActions = k.forecast_daily ?? k.daily_avg;
  if (k.days_left !== 999 && k.days_left <= 7) {
    actions.push({ priority: 100, type: 'critical', icon: '🚨', title: 'Срочное пополнение',
      text: `Сток на ${k.days_left} дн. при темпе ${effectiveRateForActions} пар/день. Заказывайте немедленно.` });
  } else if (k.days_left !== 999 && k.days_left <= 14) {
    actions.push({ priority: 80, type: 'warn', icon: '📦', title: 'Запланировать закупку',
      text: `Осталось ${k.days_left} дней при темпе ${effectiveRateForActions} пар/день. Оформите заказ в ближайшие дни.` });
  }

  if (k.return_rate_30d > 30) {
    actions.push({ priority: 70, type: 'warn', icon: '↩️', title: 'Высокий возврат',
      text: `${k.return_rate_30d}% возвратов за 30 дн. — разберитесь с причиной перед следующей закупкой.` });
  }

  if (k.is_anomaly || (k.trend_pct >= 60 && (k.sales_7d || 0) >= 5)) {
    actions.push({ priority: 75, type: 'warn', icon: '⚡', title: 'Проверьте причину роста',
      text: `Продажи выросли на +${k.trend_pct}% — возможна акция или снижение цены у поставщика. Не увеличивайте закупку, не проверив реальный спрос.` });
  } else if (k.trend_pct >= 40) {
    actions.push({ priority: 60, type: 'good', icon: '🔥', title: 'Горячий спрос',
      text: `Продажи выросли +${k.trend_pct}% за неделю. Рассмотрите увеличение закупки.` });
  } else if (k.trend_pct <= -40 && k.sales_30d > 0) {
    actions.push({ priority: 55, type: 'warn', icon: '📉', title: 'Спад продаж',
      text: `Снижение на ${Math.abs(k.trend_pct)}% за неделю. Рассмотрите скидку или паузу закупок.` });
  }

  const deficitSizes = sizes.filter(s => s.pct > 10 && s.stock_total === 0);
  if (deficitSizes.length > 0) {
    actions.push({ priority: 65, type: 'warn', icon: '📐', title: 'Дефицит по размерам',
      text: `Размеры ${deficitSizes.map(s => s.size).join(', ')} активно продаются, но сток закончился.` });
  }

  if (actions.length === 0) {
    actions.push({ priority: 0, type: 'good', icon: '✅', title: 'Всё под контролем',
      text: 'Товар работает стабильно. Поддерживайте текущий уровень стока.' });
  }

  actions.sort((a, b) => b.priority - a.priority);

  // ── Restock ───────────────────────────────────────────────────────
  const effectiveRate = k.forecast_daily ?? k.daily_avg;
  const hasStockoutAdj = k.availability_pct != null && k.availability_pct < 83 && effectiveRate !== k.daily_avg;

  // Per-size order calculation (reactive to targetDays)
  const sizeData = sizes.filter(s => s.pct > 0 || s.stock_total > 0).map(s => {
    const rate = effectiveRate * s.pct / 100;
    const idealStock = Math.ceil(rate * targetDays);
    const deficit = Math.max(0, idealStock - s.stock_total);
    const daysLeft = rate > 0.01 ? Math.floor(s.stock_total / rate) : 999;
    const excess = s.pct === 0 && s.stock_total > 0;
    return { ...s, rate, idealStock, deficit, daysLeft, status: excess ? 'excess' : deficit > 0 ? 'deficit' : 'ok' };
  });
  const totalOrderQty = sizeData.reduce((sum, s) => sum + s.deficit, 0);
  const orderQty = sizeData.length > 0 ? totalOrderQty : Math.max(0, Math.ceil(effectiveRate * targetDays) - k.total_stock);

  // Best supplier by composite score
  let bestSupplier = null;
  if (supplierPerf.length > 0) {
    const maxSales = Math.max(...supplierPerf.map(s => s.sales_30d), 1);
    const scored = supplierPerf.map(sp => {
      const si = suppliers.find(s => s.supplier === sp.supplier);
      return {
        ...sp, price: si?.price || 0, stock: si?.stock || 0,
        score: (sp.sales_30d / maxSales) * 0.5 + (1 - sp.return_rate / 100) * 0.3 + (sp.active_days / 30) * 0.2,
      };
    });
    scored.sort((a, b) => b.score - a.score);
    bestSupplier = scored[0];
  } else if (suppliers.length > 0) {
    const withStock = [...suppliers].filter(s => s.stock > 0).sort((a, b) => b.stock - a.stock);
    if (withStock.length) bestSupplier = { ...withStock[0], score: null, active_days: null };
  }

  // ── Risks ────────────────────────────────────────────────────────
  const risks = [];
  if (k.return_rate_30d > 30) risks.push({ level: 'high', text: `Возврат ${k.return_rate_30d}% — выше нормы` });
  if (suppliers.length === 1) risks.push({ level: 'med', text: 'Один поставщик — нет резервного варианта' });
  else {
    const total = suppliers.reduce((s, x) => s + x.stock, 0);
    const top = Math.max(...suppliers.map(s => s.stock));
    if (total > 0 && top / total > 0.8) risks.push({ level: 'med', text: '80%+ стока сосредоточено у одного поставщика' });
  }
  if (k.trend_pct < -30) risks.push({ level: 'med', text: `Спад продаж: −${Math.abs(k.trend_pct)}% за неделю` });
  if (data.active_days_30 < 10 && k.sales_all > 30) risks.push({ level: 'med', text: `Нестабильные продажи — только ${data.active_days_30} акт. дней из 30` });

  // ── Confidence ───────────────────────────────────────────────────
  let confidence, confCls;
  if (k.sales_all >= 200 && data.active_days_all >= 60) { confidence = 'Высокая'; confCls = 'high'; }
  else if (k.sales_all >= 30 && data.active_days_all >= 14) { confidence = 'Средняя'; confCls = 'med'; }
  else { confidence = 'Низкая — мало данных'; confCls = 'low'; }

  return (
    <div className="an-verdict-tab">

      {/* Health badge */}
      <div className={`an-verdict-health an-verdict-health--${healthCls}`}>
        <div className="an-verdict-health-label">{healthLabel}</div>
        {healthCls !== 'gray' && (
          <div className="an-verdict-health-bar">
            <div className="an-verdict-health-fill" style={{ width: `${healthScore}%` }} />
          </div>
        )}
        {healthCls !== 'gray' && <div className="an-verdict-health-score">{healthScore} / 100</div>}
      </div>

      {/* Priority action */}
      <div className="an-verdict-section">
        <div className="an-verdict-section-title">⚡ Приоритетное действие</div>
        <div className={`an-verdict-action an-verdict-action--${actions[0].type}`}>
          <span className="an-verdict-action-icon">{actions[0].icon}</span>
          <div>
            <div className="an-verdict-action-title">{actions[0].title}</div>
            <div className="an-verdict-action-text">{actions[0].text}</div>
          </div>
        </div>
        {actions.length > 1 && (
          <div className="an-verdict-actions-rest">
            {actions.slice(1).map((a, i) => (
              <div key={i} className={`an-verdict-action-sm an-verdict-action-sm--${a.type}`}>
                <span>{a.icon}</span>
                <div><b>{a.title}:</b> {a.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Restock recommendation */}
      {(orderQty > 0 || bestSupplier) && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">📦 Рекомендация по закупке</div>
          <div className="an-verdict-restock-box">
            {orderQty > 0 && (
              <div className="an-verdict-restock-row">
                <span className="an-verdict-restock-label">Заказать</span>
                <span className="an-verdict-restock-qty">{orderQty} пар</span>
                <span className="an-verdict-restock-why">≈ {targetDays} дней при {effectiveRate} пар/день · текущий сток: {k.total_stock}</span>
              </div>
            )}
            {hasStockoutAdj && (
              <div className="an-verdict-stockout-note">
                ⚠️ Товар был в наличии {k.availability_pct}% дней — скорр. темп {effectiveRate} пар/день (было {k.daily_avg})
              </div>
            )}
            {bestSupplier && (
              <div className="an-verdict-restock-row">
                <span className="an-verdict-restock-label">Лучший поставщик</span>
                <span className="an-verdict-restock-qty">{bestSupplier.supplier}</span>
                <span className="an-verdict-restock-why">
                  {bestSupplier.price > 0 ? `${bestSupplier.price} грн` : ''}
                  {bestSupplier.active_days != null ? ` · ${bestSupplier.active_days} акт.дн.` : ''}
                  {bestSupplier.return_rate != null ? ` · возврат ${pct(bestSupplier.return_rate)}` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sales chart */}
      {data.daily_chart?.length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">📈 Динамика продаж (90 дней)</div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={data.daily_chart} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="vrd-sales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#4f8cff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4f8cff" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="vrd-ret" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#ff6b6b" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#ff6b6b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#555' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#555' }} />
              <Tooltip {...TOOLTIP} />
              <Area type="monotone" dataKey="sales"   stroke="#4f8cff" fill="url(#vrd-sales)" name="Продажи"  strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="returns" stroke="#ff6b6b" fill="url(#vrd-ret)"   name="Возвраты" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Size order calculation form */}
      {sizeData.length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">📐 Расчёт заказа по размерам</div>
          <div className="an-verdict-order-form">

            {/* Controls row */}
            <div className="an-verdict-order-controls">
              <span className="an-verdict-order-label">Запас:</span>
              {[30, 45, 60, 90].map(d => (
                <button key={d}
                  className={`an-verdict-days-btn${targetDays === d ? ' an-verdict-days-btn--active' : ''}`}
                  onClick={() => setTargetDays(d)}
                >{d}д</button>
              ))}
              <span className="an-verdict-order-sep">·</span>
              <span className="an-verdict-order-label">Доставка:</span>
              {[7, 14, 21, 28].map(d => (
                <button key={d}
                  className={`an-verdict-days-btn an-verdict-days-btn--lead${leadDays === d ? ' an-verdict-days-btn--active-lead' : ''}`}
                  onClick={() => setLeadDays(d)}
                >{d}д</button>
              ))}
            </div>

            {/* Custom qty input */}
            <div className="an-verdict-qty-row">
              <span className="an-verdict-order-label">Хочу заказать:</span>
              <input
                type="number"
                min="0"
                className="an-verdict-qty-input"
                placeholder={orderQty > 0 ? `авто: ${orderQty}` : '0'}
                value={customQtyStr}
                onChange={e => setCustomQtyStr(e.target.value)}
              />
              <span className="an-verdict-order-label">пар</span>
              {customQtyStr && parseInt(customQtyStr) > 0 && (
                <button className="an-verdict-qty-clear" onClick={() => setCustomQtyStr('')}>✕ авто</button>
              )}
              {(() => {
                const effQty = (customQtyStr && parseInt(customQtyStr) > 0) ? parseInt(customQtyStr) : orderQty;
                if (effQty <= 0) return <span className="an-verdict-order-sufficient">Сток достаточен для {targetDays} дней</span>;
                return (
                  <span className="an-verdict-order-total">
                    Итого: <b>{effQty} пар</b>
                    {bestSupplier?.price > 0 && <> · ≈ <b>{fmt(Math.round(effQty * bestSupplier.price))} грн</b></>}
                  </span>
                );
              })()}
            </div>

            {/* Cascade depletion pattern alert */}
            {(() => {
              const alreadyOut    = sizeData.filter(s => s.pct > 0 && s.stock_total === 0);
              const beforeDelivery = sizeData.filter(s => s.pct > 0 && s.stock_total > 0 && s.daysLeft < leadDays);
              const duringDelivery = sizeData.filter(s => s.pct > 0 && s.stock_total > 0 && s.daysLeft >= leadDays && s.daysLeft < leadDays * 2);
              if (alreadyOut.length === 0 && beforeDelivery.length === 0) return null;
              return (
                <div className="an-depl-pattern">
                  <div className="an-depl-pattern-hdr">
                    <span>🔍</span>
                    <span className="an-depl-pattern-title">Паттерн каскадного истощения</span>
                    <span className="an-depl-pattern-sub">Заказывайте все дефицитные размеры за раз, не только пустые</span>
                  </div>
                  <div className="an-depl-rows">
                    {alreadyOut.length > 0 && (
                      <div className="an-depl-row">
                        <span className="an-depl-dot an-depl-dot--out" />
                        <span className="an-depl-row-label">Уже нет стока:</span>
                        <span className="an-depl-sizes an-depl-sizes--out">{alreadyOut.map(s => s.size).join(', ')}</span>
                      </div>
                    )}
                    {beforeDelivery.length > 0 && (
                      <div className="an-depl-row">
                        <span className="an-depl-dot an-depl-dot--before" />
                        <span className="an-depl-row-label">Закончатся до доставки ({leadDays}д):</span>
                        <span className="an-depl-sizes an-depl-sizes--before">{beforeDelivery.map(s => `${s.size} (${s.daysLeft}д)`).join(', ')}</span>
                      </div>
                    )}
                    {duringDelivery.length > 0 && (
                      <div className="an-depl-row">
                        <span className="an-depl-dot an-depl-dot--during" />
                        <span className="an-depl-row-label">Закончатся во время ожидания:</span>
                        <span className="an-depl-sizes an-depl-sizes--during">{duringDelivery.map(s => `${s.size} (${s.daysLeft}д)`).join(', ')}</span>
                      </div>
                    )}
                  </div>
                  {(beforeDelivery.length > 0 || duringDelivery.length > 0) && (
                    <div className="an-depl-tip">
                      ⚠️ Типичная ошибка: заказать только пустые размеры.
                      {[...beforeDelivery, ...duringDelivery].length > 0 && (
                        <> Размеры <b>{[...beforeDelivery, ...duringDelivery].map(s => s.size).join(', ')}</b> закончатся пока едет заказ — включите их в текущий заказ.</>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* В закупку — inline форма */}
            {isAdmin && sizeData.length > 0 && (
              <div className="an-verdict-order-actions">
                {!showOrderForm ? (
                  <div className="an-order-trigger-row">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        const customNum = customQtyStr && parseInt(customQtyStr) > 0 ? parseInt(customQtyStr) : null;
                        const sizeMap = {};
                        sizeData.forEach(s => {
                          const qty = customNum ? Math.round(customNum * s.pct / 100) : s.deficit;
                          sizeMap[String(s.size)] = qty > 0 ? qty : 0;
                        });
                        const allSizes = ['35','36','37','38','39','40','41','42','43','44','45','46','47'];
                        const sizesQty = {};
                        allSizes.forEach(sz => { sizesQty[sz] = sizeMap[sz] ?? 0; });
                        setOrderFormSizes(sizesQty);
                        setShowOrderForm(true);
                        setOrderAdded(false);
                      }}
                    >
                      + В закупку Cheese
                    </button>
                    {orderAdded && (
                      <span className="an-order-added-ok">✓ Добавлено в закупку</span>
                    )}
                  </div>
                ) : (
                  <div className="an-order-inline-form">
                    <div className="an-order-inline-header">
                      <span className="an-order-inline-title">Закупка: {data.name || art}</span>
                      {art && <span className="an-order-inline-sku">{art}</span>}
                      <button className="an-order-inline-close" onClick={() => setShowOrderForm(false)}>✕</button>
                    </div>

                    <div className="an-order-inline-table-wrap">
                      <table className="an-order-inline-table">
                        <thead>
                          <tr>
                            {Object.keys(orderFormSizes).map(size => (
                              <th key={size} className="an-order-inline-th">{size}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {Object.entries(orderFormSizes).map(([size, qty]) => (
                              <td key={size} className="an-order-inline-td">
                                <input
                                  type="number"
                                  min="0"
                                  className="an-order-inline-qty"
                                  value={qty}
                                  onChange={e => {
                                    const v = Math.max(0, parseInt(e.target.value) || 0);
                                    setOrderFormSizes(prev => ({ ...prev, [size]: v }));
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="an-order-inline-total">
                      Итого: <b>{Object.values(orderFormSizes).reduce((a, b) => a + Number(b), 0)} пар</b>
                    </div>

                    <div className="an-order-inline-footer">
                      <label className="an-order-box-check an-order-box-check--lg">
                        <input type="checkbox" checked={orderHasBox} onChange={e => setOrderHasBox(e.target.checked)} />
                        BOX
                      </label>
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowOrderForm(false)}>
                        Отмена
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={orderAdding || Object.keys(orderFormSizes).length === 0}
                        onClick={async () => {
                          setOrderAdding(true);
                          try {
                            await cheeseAPI.createOrder({
                              name: data.name || art || '',
                              photo_url: data.photo || null,
                              catalog_sku: art || null,
                              sizes_qty: orderFormSizes,
                              has_box: orderHasBox,
                            });
                            setShowOrderForm(false);
                            setOrderHasBox(false);
                            setOrderAdded(true);
                            setTimeout(() => setOrderAdded(false), 3000);
                          } catch (e) {
                            alert('Ошибка: ' + (e?.response?.data?.detail || e.message));
                          } finally {
                            setOrderAdding(false);
                          }
                        }}
                      >
                        {orderAdding ? 'Добавляем…' : 'Добавить в закупку'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Size table */}
            <div className="an-table-wrap">
              <table className="an-table an-verdict-size-table">
                <thead>
                  <tr>
                    <th>Размер</th>
                    <th title="Текущее наличие">Сток</th>
                    <th title="Доля в продажах">Доля</th>
                    <th title="Средний темп продаж, пар/день">Темп</th>
                    <th title="Дней стока при текущем темпе">Дней</th>
                    <th title={`Кол-во к заказу для запаса на ${targetDays} дн.`}>Заказать</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const customNum = customQtyStr && parseInt(customQtyStr) > 0 ? parseInt(customQtyStr) : null;
                    let totalDisplayed = 0;
                    const rows = sizeData.map(s => {
                      const displayOrder = customNum
                        ? Math.round(customNum * s.pct / 100)
                        : s.deficit;
                      totalDisplayed += displayOrder;
                      const rowCls = s.stock_total === 0 && s.pct > 0 ? 'an-verdict-sz-row--out'
                        : s.daysLeft < leadDays && s.pct > 0 ? 'an-verdict-sz-row--alarm'
                        : s.daysLeft < leadDays * 2 && s.pct > 0 ? 'an-verdict-sz-row--deficit'
                        : s.status === 'excess' ? 'an-verdict-sz-row--excess'
                        : '';
                      const daysCls = s.stock_total === 0 ? 'an-bad'
                        : s.daysLeft < leadDays ? 'an-bad'
                        : s.daysLeft < leadDays * 2 ? 'an-warn'
                        : s.daysLeft >= 999 ? 'an-ok' : 'an-ok';
                      const underCovers = customNum && displayOrder < s.deficit;
                      return (
                        <tr key={s.size} className={rowCls}>
                          <td className="an-verdict-sz-name">{s.size}</td>
                          <td>{s.stock_total}</td>
                          <td>{s.pct > 0 ? `${s.pct}%` : '—'}</td>
                          <td>{s.rate > 0.01 ? s.rate.toFixed(2) : '—'}</td>
                          <td className={daysCls}>
                            {s.stock_total === 0 ? '0' : s.daysLeft >= 999 ? '∞' : s.daysLeft}
                          </td>
                          <td className={`an-verdict-sz-order${displayOrder > 0 ? ' an-verdict-sz-order--needed' : ''}${underCovers ? ' an-verdict-sz-order--under' : ''}`}>
                            {displayOrder > 0
                              ? <><b>+{displayOrder}</b>{underCovers && <span className="an-sz-under" title={`Нужно +${s.deficit}, покрывает не полностью`}> ⚠</span>}</>
                              : <span className="an-verdict-sz-ok">✓</span>
                            }
                          </td>
                        </tr>
                      );
                    });
                    return (
                      <>
                        {rows}
                        {totalDisplayed > 0 && (
                          <tr className="an-verdict-sz-total-row">
                            <td colSpan={5} className="an-verdict-sz-total-label">
                              Итого к заказу ({customNum ? 'вручную' : `${targetDays} дн.`})
                            </td>
                            <td><b>{totalDisplayed} пар</b></td>
                          </tr>
                        )}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Size profile from sales */}
      {sizeData.filter(s => s.pct > 0).length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">📊 Размерный профиль продаж</div>
          {(() => {
            const activeSizes = sizeData.filter(s => s.pct > 0 || s.stock_total > 0);
            const maxSales = Math.max(...activeSizes.map(s => s.pct), 1);
            const maxStock = Math.max(...activeSizes.map(s => s.stock_total), 1);
            return (
              <div className="an-size-profile">
                {activeSizes.map(s => (
                  <div key={s.size} className="an-size-profile-item">
                    <div className="an-size-profile-bars">
                      <div
                        className="an-size-profile-bar an-size-profile-bar--sales"
                        style={{ height: `${Math.round(s.pct / maxSales * 52)}px` }}
                        title={`Продажи: ${s.pct}%`}
                      />
                      <div
                        className="an-size-profile-bar an-size-profile-bar--stock"
                        style={{ height: `${Math.round(s.stock_total / maxStock * 52)}px` }}
                        title={`Сток: ${s.stock_total} шт`}
                      />
                    </div>
                    <div className="an-size-profile-sz">{s.size}</div>
                    <div className="an-size-profile-pct">{s.pct > 0 ? `${s.pct}%` : '—'}</div>
                    <div className="an-size-profile-stk">
                      {s.stock_total > 0 ? s.stock_total : <span className="an-size-profile-oos">0</span>}
                    </div>
                  </div>
                ))}
                <div className="an-size-profile-legend">
                  <span><span className="an-size-legend-dot" style={{ background: 'var(--lg-accent)' }} />Продажи</span>
                  <span><span className="an-size-legend-dot" style={{ background: '#4ecdc4' }} />Сток</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Seasonality mini chart */}
      {data.seasonal?.length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">📅 Сезонність</div>
          {(() => {
            const now = new Date();
            const curYear  = now.getFullYear();
            const curMonth = now.getMonth() + 1; // 1-12
            const nextMonth = curMonth === 12 ? 1 : curMonth + 1;
            const nextMonthYear = curMonth === 12 ? curYear + 1 : curYear;

            const years = [...new Set(data.seasonal.map(s => s.year))].sort().slice(-2);
            const hasStock = data.seasonal.some(s => s.avg_stock != null);
            const YEAR_COLORS  = ['#4ecdc4', 'var(--lg-accent)'];
            const STOCK_COLORS = ['rgba(255,230,109,0.9)', 'rgba(249,115,22,0.9)'];

            // — Forecast: avg sales for next calendar month across all years —
            const nextMonthSales = data.seasonal.filter(s => s.month === nextMonth && s.sales > 0);
            const forecastAvg = nextMonthSales.length
              ? Math.round(nextMonthSales.reduce((a, s) => a + s.sales, 0) / nextMonthSales.length)
              : null;

            // — Lost sales: months with confirmed stock but 0 sales —
            const lostMonths = data.seasonal.filter(s => s.avg_stock != null && s.avg_stock > 0 && s.sales === 0);

            // — Purchases lookup by (yr, mo) —
            const purchasesByYrMo = {};
            (data.purchases || []).forEach(p => {
              const key = `${p.yr}_${p.mo}`;
              if (!purchasesByYrMo[key]) {
                purchasesByYrMo[key] = { qty: 0, dates: [], isReceived: p.is_received };
              }
              purchasesByYrMo[key].qty += p.qty;
              purchasesByYrMo[key].dates.push(p.date);
              if (p.is_received) purchasesByYrMo[key].isReceived = true;
            });
            const hasPurchases = Object.keys(purchasesByYrMo).length > 0;

            // — Chart data —
            const chartData = MONTH_SHORT.map((mname, mi) => {
              const mo = mi + 1;
              const row = { month: mname, _mo: mo };
              years.forEach(yr => {
                const found = data.seasonal.find(s => s.year === yr && s.month === mo);
                row[String(yr)]         = found ? found.sales    : 0;
                row[`avg_stock_${yr}`]  = found?.avg_stock  ?? null;
                row[`max_stock_${yr}`]  = found?.max_stock  ?? null;
                // flag: confirmed stocked but zero sales
                row[`lost_${yr}`] = (found?.avg_stock != null && found.avg_stock > 0 && (found?.sales ?? 0) === 0);
                row[`purchase_${yr}`]   = purchasesByYrMo[`${yr}_${mo}`] || null;
              });
              return row;
            });

            return (
              <>
                <ResponsiveContainer width="100%" height={hasStock ? 210 : 160}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: hasStock ? 32 : 4, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#888' }} />
                    <YAxis yAxisId="sales" tick={{ fontSize: 11, fill: '#555' }} allowDecimals={false} />
                    {hasStock && (
                      <YAxis yAxisId="stock" orientation="right"
                        tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => v || ''} width={28} />
                    )}
                    <Tooltip
                      {...TOOLTIP}
                      itemStyle={{ color: 'var(--lg-text)' }}
                      formatter={(value, name, props) => {
                        if (name.startsWith('Наличие')) {
                          const yr = parseInt(name.split(' ')[1]);
                          const purchase = props.payload?.[`purchase_${yr}`];
                          let label = `${value} шт.`;
                          if (purchase) {
                            const status = purchase.isReceived ? 'получено' : 'в пути';
                            label += ` · 📦 закупка ${purchase.dates.join(', ')}: ${purchase.qty} пар (${status})`;
                          }
                          return [label, `${name} (ср.)`];
                        }
                        const yr = parseInt(name);
                        if (!isNaN(yr)) {
                          const entry = props.payload;
                          const avgStk = entry?.[`avg_stock_${yr}`];
                          const maxStk = entry?.[`max_stock_${yr}`];
                          const conv = (avgStk > 0 && value > 0)
                            ? ` · конв. ${(value / avgStk * 100).toFixed(0)}%` : '';
                          const stockInfo = avgStk != null
                            ? ` (наличие: ср. ${avgStk} / макс. ${maxStk}${conv})`
                            : '';
                          if (entry?.[`lost_${yr}`])
                            return [`${value} пар — ⚠️ был сток, нет продаж${stockInfo}`, String(yr)];
                          return [`${value} пар${stockInfo}`, String(yr)];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ color: 'var(--lg-text-3)', fontSize: 11 }} />
                    {years.map((yr, i) => (
                      <Bar key={yr} yAxisId="sales" dataKey={String(yr)} name={String(yr)}
                        fill={YEAR_COLORS[i % YEAR_COLORS.length]}
                        radius={[2, 2, 0, 0]}
                      >
                        {chartData.map((entry, idx) => (
                          <Cell key={idx}
                            fill={entry[`lost_${yr}`] ? '#ff6b35' : YEAR_COLORS[i % YEAR_COLORS.length]}
                            opacity={entry[`lost_${yr}`] ? 0.85 : 1}
                          />
                        ))}
                      </Bar>
                    ))}
                    {hasStock && years.map((yr, i) => (
                      <Line key={`stock_${yr}`} yAxisId="stock"
                        dataKey={`avg_stock_${yr}`}
                        name={`Наличие ${yr}`}
                        stroke={STOCK_COLORS[i % STOCK_COLORS.length]}
                        strokeWidth={2} strokeDasharray="4 2"
                        connectNulls={false}
                        dot={(dotProps) => {
                          const { cx, cy, payload, key: dk } = dotProps;
                          if (!cx || !cy) return null;
                          const purchase = payload[`purchase_${yr}`];
                          if (purchase) {
                            const col = purchase.isReceived ? '#22c55e' : '#f59e0b';
                            return (
                              <g key={`pd-${yr}-${payload._mo}`}>
                                <circle cx={cx} cy={cy} r={7} fill={col} stroke="white" strokeWidth={1.5} />
                                <text x={cx} y={cy + 4} textAnchor="middle" fontSize={9} fill="white" fontWeight="bold">₴</text>
                              </g>
                            );
                          }
                          return <circle key={dk} cx={cx} cy={cy} r={2.5} fill={STOCK_COLORS[i % STOCK_COLORS.length]} />;
                        }}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Purchase markers legend */}
                {hasPurchases && (
                  <div className="an-season-purchases-legend">
                    <span className="an-season-purchases-dot an-season-purchases-dot--received" />
                    <span>Закупка получена</span>
                    <span className="an-season-purchases-dot an-season-purchases-dot--transit" />
                    <span>В пути</span>
                  </div>
                )}

                {/* Forecast next month */}
                {forecastAvg != null && (
                  <div className="an-season-forecast">
                    <span className="an-season-forecast-label">
                      📈 Прогноз {MONTH_SHORT[nextMonth - 1]} {nextMonthYear}:
                    </span>
                    <span className="an-season-forecast-val">~{forecastAvg} пар</span>
                    <span className="an-season-forecast-note">
                      среднее за {nextMonthSales.length} {nextMonthSales.length === 1 ? 'год' : 'года/лет'}
                    </span>
                  </div>
                )}

                {/* Lost sales alert */}
                {lostMonths.length > 0 && (
                  <div className="an-season-lost">
                    <span className="an-season-lost-title">⚠️ Был сток, не продалось:</span>
                    {lostMonths.map(s => {
                      const histSales = data.seasonal
                        .filter(h => h.month === s.month && h.year !== s.year && h.sales > 0);
                      const expected = histSales.length
                        ? Math.round(histSales.reduce((a, h) => a + h.sales, 0) / histSales.length)
                        : null;
                      return (
                        <span key={`${s.year}-${s.month}`} className="an-season-lost-item">
                          {s.month_name} {s.year}
                          {expected != null ? ` (обычно ~${expected} пар)` : ''}
                        </span>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Size detail table */}
      {(() => {
        const sbp = data.size_by_period || {};
        const SALE_SIZES = [36,37,38,39,40,41,42,43,44,45,46];
        const activeSzDet = SALE_SIZES.filter(s =>
          (sbp.month || {})[s] > 0 || (sbp.week || {})[s] > 0 || parseInt(data.size_totals?.[String(s)] || 0) > 0
        );
        if (activeSzDet.length === 0) return null;
        const totalMonth = activeSzDet.reduce((sum, s) => sum + ((sbp.month || {})[s] || 0), 0);
        return (
          <div className="an-verdict-section">
            <div className="an-verdict-section-title">📋 Продажи по размерам</div>
            <div className="an-table-wrap">
              <table className="an-table">
                <thead>
                  <tr>
                    <th>Размер</th>
                    <th title="Сегодня">Сег.</th>
                    <th title="За неделю">Нед.</th>
                    <th title="За месяц">Мес.</th>
                    <th title="Доля за месяц">%</th>
                    <th title="Текущий сток">Сток</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSzDet.map(s => {
                    const today = (sbp.today || {})[s] || 0;
                    const week  = (sbp.week  || {})[s] || 0;
                    const month = (sbp.month || {})[s] || 0;
                    const stock = parseInt(data.size_totals?.[String(s)] || 0);
                    const mPct  = totalMonth > 0 ? (month / totalMonth * 100).toFixed(1) : '—';
                    const noStk = stock === 0 && month > 0;
                    return (
                      <tr key={s} className={noStk ? 'an-size-warn-row' : ''}>
                        <td><b>{s}</b></td>
                        <td className="an-num">{today > 0 ? today : '—'}</td>
                        <td className="an-num">{week  > 0 ? week  : '—'}</td>
                        <td className="an-num">{month > 0 ? month : '—'}</td>
                        <td className="an-num">{month > 0 ? `${mPct}%` : '—'}</td>
                        <td className={`an-num${stock === 0 ? ' an-bad' : ''}`}>{stock > 0 ? stock : <b>0</b>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Supplier performance + stock */}
      {supplierPerf.length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">🏆 Поставщики — продажи и склад</div>
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>#</th><th>Поставщик</th>
                  <th title="Продажи за 30 дней">Прод. 30д</th>
                  <th title="Активных дней">Акт.дн.</th>
                  <th title="Возвраты за 30 дней">Возвр.</th>
                  <th title="% возвратов">% возвр.</th>
                  <th title="Цена">Цена</th>
                  <th title="Текущий сток">Сток</th>
                </tr>
              </thead>
              <tbody>
                {supplierPerf.map((sp, i) => {
                  const si = suppliers.find(s => s.supplier === sp.supplier);
                  return (
                    <tr key={sp.supplier} className={i === 0 ? 'an-verdict-best-row' : ''}>
                      <td className="an-rank">{i + 1}</td>
                      <td>{sp.supplier}{i === 0 && <span className="an-verdict-best-badge"> ★</span>}</td>
                      <td className="an-num"><b>{fmt(sp.sales_30d)}</b></td>
                      <td className="an-num">{sp.active_days}д</td>
                      <td className="an-num">{fmt(sp.returns_30d)}</td>
                      <td className={`an-num an-pct${sp.return_rate > 30 ? ' an-bad' : sp.return_rate > 15 ? ' an-warn' : ' an-ok'}`}>
                        {pct(sp.return_rate)}
                      </td>
                      <td className="an-num">{si?.price > 0 ? `${fmt(si.price)} грн` : '—'}</td>
                      <td className="an-num">{fmt(si?.stock || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stock per supplier per size */}
      {(() => {
        const withStock = suppliers.filter(s => s.stock > 0);
        if (withStock.length === 0) return null;
        const activeSzStock = _getAllActiveSizes(data);
        return (
          <div className="an-verdict-section">
            <div className="an-verdict-section-title">📦 Склад по поставщикам</div>
            <div className="an-table-wrap">
              <table className="an-table an-stock-table">
                <thead>
                  <tr>
                    <th>Поставщик</th>
                    <th>Цена</th>
                    <th>Всего</th>
                    {activeSzStock.map(s => <th key={s} className="an-size-th">{s}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {withStock.map(sup => (
                    <tr key={sup.supplier}>
                      <td className="an-name-cell">{sup.supplier}</td>
                      <td className="an-num">{sup.price > 0 ? `${fmt(sup.price)} грн` : '—'}</td>
                      <td className="an-num an-stock-total"><b>{fmt(sup.stock)}</b></td>
                      {activeSzStock.map(s => {
                        const qty = sup.sizes[String(s)] || 0;
                        return (
                          <td key={s} className={`an-size-cell${qty === 0 ? ' an-size-cell--empty' : ''}`}>
                            {qty > 0 ? qty : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="an-stock-total-row">
                    <td colSpan={2}><b>Итого</b></td>
                    <td className="an-num"><b>{fmt(k.total_stock)}</b></td>
                    {activeSzStock.map(s => {
                      const qty = parseInt(data.size_totals?.[String(s)] || 0);
                      return (
                        <td key={s} className={`an-size-cell${qty === 0 ? ' an-size-cell--empty' : ' an-size-cell--total'}`}>
                          {qty > 0 ? <b>{qty}</b> : '—'}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Price range */}
      {k.price_min > 0 && k.price_max > k.price_min && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">💰 Ценовой диапазон</div>
          <div className="an-price-range-row">
            <span className="an-price-label">Мин</span>
            <span className="an-price-val">{fmt(k.price_min)} грн</span>
            <div className="an-price-bar-wrap">
              <div className="an-price-bar" style={{ width: '100%' }}>
                <div className="an-price-bar-fill" style={{
                  marginLeft: 0,
                  width: `${((k.price_avg - k.price_min) / (k.price_max - k.price_min)) * 100}%`,
                }} />
              </div>
            </div>
            <span className="an-price-val">{fmt(k.price_max)} грн</span>
            <span className="an-price-label">Макс</span>
          </div>
          <div className="an-price-avg">Средняя цена: <b>{fmt(k.price_avg)} грн</b></div>
        </div>
      )}

      {/* Risks */}
      {risks.length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">⚠️ Факторы риска</div>
          <div className="an-verdict-risks">
            {risks.map((r, i) => (
              <div key={i} className={`an-verdict-risk an-verdict-risk--${r.level}`}>
                {r.level === 'high' ? '🔴' : '🟡'} {r.text}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent sales log */}
      {(data.sales_log || []).length > 0 && (
        <div className="an-verdict-section">
          <div className="an-verdict-section-title">🗓 Последние продажи по поставщикам</div>
          <ReportSalesLogTab data={data} limit={30} />
        </div>
      )}

      {/* Data confidence */}
      <div className={`an-verdict-confidence an-verdict-confidence--${confCls}`}>
        <span className="an-verdict-conf-label">Точность рекомендаций:</span>
        <span className="an-verdict-conf-val">{confidence}</span>
        <span className="an-verdict-conf-basis">{fmt(k.sales_all)} продаж · {data.active_days_all} дней в продаже</span>
      </div>

    </div>
  );
}

// Цвета поставщиков для графика цен
const SUP_COLORS = ['#4f8cff','#4ecdc4','#ffe66d','#a78bfa','#f97316','#ff6b6b','#34d399','#fb7185'];

function ReportPricesTab({ art }) {
  const { data, loading, error } = useCached(
    () => analyticsAPI.getPriceHistory(art),
    `price_history:${art}`,
  );

  if (loading) return <Loader />;
  if (error)   return <ErrBox />;
  if (!data || data.history.length === 0) {
    return (
      <div style={{ padding: '24px 0', color: 'var(--lg-text-2)' }}>
        <div style={{ fontSize: 15, marginBottom: 8 }}>История цен пока не накоплена</div>
        <div style={{ fontSize: 13, color: 'var(--lg-text-3)' }}>
          Снимки цен берутся раз в сутки и сохраняются автоматически.
          Первые данные появятся завтра после запуска обновления аналитики.
        </div>
      </div>
    );
  }

  const { history, suppliers } = data;
  // Определяем значимые изменения цены (drop ≥ 5 %) для аннотаций
  const drops = [];
  suppliers.forEach(sup => {
    for (let i = 1; i < history.length; i++) {
      const prev = history[i - 1][sup];
      const curr = history[i][sup];
      if (prev && curr && curr < prev * 0.95) {
        drops.push({ date: history[i].date, sup, from: prev, to: curr,
          pct: Math.round((prev - curr) / prev * 100) });
      }
    }
  });

  return (
    <div className="an-prices-tab">
      {drops.length > 0 && (
        <div className="an-prices-drops">
          <div className="an-prices-drops-title">⚡ Снижения цен</div>
          {drops.map((d, i) => (
            <div key={i} className="an-prices-drop-row">
              <span className="an-prices-drop-date">{d.date}</span>
              <span className="an-prices-drop-sup">{d.sup}</span>
              <span className="an-prices-drop-val">
                {fmt(d.from)} → <b>{fmt(d.to)}</b> грн
              </span>
              <span className="an-promo-flag">−{d.pct}%</span>
            </div>
          ))}
        </div>
      )}

      <div className="an-report-section-title" style={{ marginTop: drops.length ? 20 : 0 }}>
        Динамика цен по поставщикам ({data.days} дн.)
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={history} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#555' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11, fill: '#555' }} unit=" грн" />
          <Tooltip {...TOOLTIP}
            formatter={(v, name) => v != null ? [`${fmt(v)} грн`, name] : [null, name]}
          />
          <Legend wrapperStyle={{ color: 'var(--lg-text-3)', fontSize: 12 }} />
          {/* Рыночный минимум — пунктирная линия */}
          <Line dataKey="market_min" name="Рынок мин" stroke="#666"
            strokeDasharray="4 2" strokeWidth={1} dot={false} connectNulls />
          {/* Рыночный средний */}
          <Line dataKey="market_avg" name="Рынок avg" stroke="#888"
            strokeDasharray="2 2" strokeWidth={1} dot={false} connectNulls />
          {/* Каждый поставщик — своим цветом */}
          {suppliers.map((sup, i) => (
            <Line key={sup} dataKey={sup} name={sup}
              stroke={SUP_COLORS[i % SUP_COLORS.length]}
              strokeWidth={2} dot={false} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Сводная таблица: текущие цены */}
      <div className="an-report-section-title" style={{ marginTop: 24 }}>Текущие цены</div>
      {(() => {
        const last = history[history.length - 1];
        if (!last) return null;
        const rows = suppliers.map(sup => ({ sup, price: last[sup] })).filter(r => r.price != null);
        rows.sort((a, b) => a.price - b.price);
        const minP = rows[0]?.price;
        const maxP = rows[rows.length - 1]?.price;
        return (
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr><th>Поставщик</th><th>Цена сейчас</th><th>% от мин.</th></tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.sup}>
                    <td>{r.sup}</td>
                    <td className="an-num">{fmt(r.price)} грн</td>
                    <td className={`an-num ${r.price === minP ? 'an-ok' : r.price >= maxP * 0.98 ? 'an-warn' : ''}`}>
                      {minP ? `+${Math.round((r.price - minP) / minP * 100)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}

const REPORT_TABS = [
  { key: 'verdict',    label: '⚡ Вердикт' },
  { key: 'sales',      label: 'Продажи' },
  { key: 'log',        label: '📋 Журнал' },
  { key: 'prices',     label: '💰 Цены' },
  { key: 'stock',      label: 'Склад' },
  { key: 'suppliers',  label: 'Поставщики' },
  { key: 'seasonal',   label: 'Сезонность' },
  { key: 'sizes',      label: 'По размерам' },
];

function ProductReportModal({ art, onClose }) {
  const [tab, setTab] = useState('verdict');
  const { data, loading, error } = useCached(
    () => analyticsAPI.getProductReport(art),
    `product_report:${art}`,
  );
  const cheeseStatus = useCheeseStatus(art ? [art] : []);

  const k = data?.kpi;

  return (
    <div className="an-modal-overlay" onClick={onClose}>
      <div className="an-product-report" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="an-report-hdr">
          <div className="an-report-hdr-left">
            {data?.photo
              ? <img className="an-report-big-photo" src={proxyImageUrl(data.photo)} alt="" />
              : <div className="an-report-big-photo an-report-big-photo--empty" />
            }
            <div className="an-report-hdr-info">
              <div className="an-report-hdr-art">{art}</div>
              <div className="an-report-hdr-name">{data?.name || art}</div>
              <CheeseStatusBadge statuses={cheeseStatus[art]} />
              {data?.first_sale && (
                <div className="an-report-hdr-meta">
                  Первая продажа: {data.first_sale}
                  {data.active_days_all > 0 && ` · ${data.active_days_all} дней в продаже`}
                </div>
              )}
            </div>
          </div>
          <button className="an-modal-close an-report-close" onClick={onClose}>✕</button>
        </div>

        {/* KPI tiles */}
        {k && (
          <div className="an-report-kpis">
            <KpiTile label="Сток"
              value={fmt(k.total_stock)}
              sub={k.days_left >= 999 ? '∞ дней' : `${k.days_left} дн.`}
              color={k.days_left <= 7 ? 'red' : k.days_left <= 14 ? 'orange' : 'green'} />
            <KpiTile label="Сегодня"
              value={fmt(k.sales_today)}
              sub={k.ret_today > 0 ? `↩ ${k.ret_today}` : 'продаж'}
              color="blue" />
            <KpiTile label="7 дней"
              value={fmt(k.sales_7d)}
              sub={<TrendArrow pct={k.trend_pct} />}
              color="blue" />
            <KpiTile label="30 дней"
              value={fmt(k.sales_30d)}
              sub={`↩ ${k.ret_30d}`}
              color="blue" />
            <KpiTile label="Всего"
              value={fmt(k.sales_all)}
              sub="за всё время"
              color="purple" />
            <KpiTile label="Возвраты"
              value={pct(k.return_rate_30d)}
              sub="за 30 дней"
              color={k.return_rate_30d > 30 ? 'red' : k.return_rate_30d > 15 ? 'orange' : 'green'} />
            <KpiTile label="Ср/день"
              value={k.daily_avg}
              sub="пар в день"
              color="gray" />
          </div>
        )}

        {/* Sub-tabs */}
        <div className="an-report-tab-nav">
          {REPORT_TABS.map(t => (
            <button key={t.key}
              className={`an-report-tab-btn${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="an-report-content">
          {loading && <Loader />}
          {error   && <ErrBox />}
          {data && tab === 'verdict'   && <ReportVerdictTab   data={data} art={art} />}
          {data && tab === 'sales'     && <ReportSalesTab     data={data} />}
          {data && tab === 'log'       && <ReportSalesLogTab  data={data} />}
          {tab === 'prices'            && <ReportPricesTab    art={art} />}
          {data && tab === 'stock'     && <ReportStockTab     data={data} />}
          {data && tab === 'suppliers' && <ReportSuppliersTab data={data} />}
          {data && tab === 'seasonal'  && <ReportSeasonalTab  data={data} />}
          {data && tab === 'sizes'     && <ReportSizesTab     data={data} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Обзор
// ---------------------------------------------------------------------------

function OverviewTab() {
  const { data, loading, stale, error } = useCached(
    () => analyticsAPI.getSummary(), 'summary',
  );

  const [chartPeriod, setChartPeriod] = useState(90);
  const [chartGroup,  setChartGroup]  = useState('day');

  const chart = useCached(
    () => analyticsAPI.getSalesChart({ period: chartPeriod, group: chartGroup }),
    `chart:${chartPeriod}:${chartGroup}`,
  );

  const seasonal = useCached(() => analyticsAPI.getSeasonal(), 'seasonal');
  const years = seasonal.data
    ? [...new Set(seasonal.data.flatMap(m => Object.keys(m).filter(k => /^\d{4}$/.test(k))))].sort()
    : [];

  const retRate = data
    ? (data.sales.sales_month ? Math.round(data.returns.back_month / data.sales.sales_month * 100) : 0)
    : 0;

  return (
    <div>
      {/* Summary cards */}
      <Section title="Ключевые показатели" stale={stale}>
        {loading && <Loader />}
        {error   && <ErrBox />}
        {data && (
          <div className="an-cards">
            <div className="an-card" style={{ borderTop: '3px solid var(--lg-accent)' }}>
              <div className="an-card-val">{fmt(data.sales.sales_today)}</div>
              <div className="an-card-label">Продаж сегодня</div>
            </div>
            <div className="an-card" style={{ borderTop: '3px solid #4ecdc4' }}>
              <div className="an-card-val">{fmt(data.sales.sales_week)}</div>
              <div className="an-card-label">За 7 дней</div>
            </div>
            <div className="an-card" style={{ borderTop: '3px solid var(--lg-accent)' }}>
              <div className="an-card-val">{fmt(data.sales.sales_month)}</div>
              <div className="an-card-label">За 30 дней</div>
            </div>
            <div className="an-card" style={{ borderTop: '3px solid var(--lg-yellow)' }}>
              <div className="an-card-val">{fmt(data.sales.sales_all)}</div>
              <div className="an-card-label">Всего продаж</div>
            </div>
            <div className="an-card" style={{ borderTop: '3px solid var(--lg-red)' }}>
              <div className="an-card-val">{fmt(data.returns.back_month)}</div>
              <div className="an-card-label">Возвратов за 30д</div>
              <div className="an-card-sub">{retRate}% от продаж</div>
            </div>
            <div className="an-card" style={{ borderTop: '3px solid #f97316' }}>
              <div className="an-card-val">{fmt(data.suppliers_count)}</div>
              <div className="an-card-label">Поставщиков</div>
            </div>
          </div>
        )}
      </Section>

      {/* Chart */}
      <Section title="Динамика продаж" stale={chart.stale}
        action={
          <div className="an-chart-controls">
            <PeriodPicker value={chartPeriod} onChange={setChartPeriod} options={PERIOD_OPTS} />
            <PeriodPicker value={chartGroup}  onChange={setChartGroup}  options={GROUP_OPTS} />
          </div>
        }
      >
        {chart.loading && <Loader />}
        {chart.error   && <ErrBox />}
        {chart.data && (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chart.data}>
              <defs>
                <linearGradient id="gS" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#4f8cff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4f8cff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#ff6b6b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ff6b6b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
              <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#666' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 12, fill: '#666' }} />
              <Tooltip {...TOOLTIP} />
              <Legend wrapperStyle={{ color: 'var(--lg-text-2)', fontSize: 13 }} />
              <Area type="monotone" dataKey="sales"   stroke="#4f8cff" fill="url(#gS)" name="Продажи"  dot={false} />
              <Area type="monotone" dataKey="returns" stroke="#ff6b6b" fill="url(#gR)" name="Возвраты" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Section>

      {/* Seasonal */}
      <Section title="Сезонность" stale={seasonal.stale}>
        {seasonal.loading && <Loader />}
        {seasonal.error   && <ErrBox />}
        {seasonal.data && (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={seasonal.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#666' }} />
              <YAxis tick={{ fontSize: 12, fill: '#666' }} />
              <Tooltip {...TOOLTIP} />
              <Legend wrapperStyle={{ color: 'var(--lg-text-2)', fontSize: 13 }} />
              {years.map((y, i) => (
                <Bar key={y} dataKey={y} name={y} fill={COLORS[i % COLORS.length]} radius={[3,3,0,0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Товары
// ---------------------------------------------------------------------------

function ProductsTab({ onForecast, onReport }) {
  const [period, setPeriod] = useState(30);

  const top = useCached(
    () => analyticsAPI.getTopProducts({ period, limit: 50 }),
    `top:${period}`,
  );

  const topSkus = top.data ? top.data.map(p => p.art) : [];
  const cheeseStatus = useCheeseStatus(topSkus);

  const worstReturns = top.data
    ? [...top.data].filter(p => p.sales >= 5).sort((a, b) => b.return_rate - a.return_rate).slice(0, 20)
    : [];

  return (
    <div>
      <Section title="Топ продаж" stale={top.stale}
        action={<PeriodPicker value={period} onChange={setPeriod} options={PERIOD_OPTS} />}
      >
        {top.loading && <Loader />}
        {top.error   && <ErrBox />}
        {top.data && (
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr><th>#</th><th colSpan={2}>Товар</th><th>Продажи</th><th>Возвраты</th><th>% возв.</th><th /></tr>
              </thead>
              <tbody>
                {top.data.map((p, i) => (
                  <tr key={p.art}>
                    <td className="an-rank">{i + 1}</td>
                    <td colSpan={2}>
                      <ProductCell photo={p.photo} art={p.art} name={p.name}
                        onClick={onReport ? () => onReport(p.art) : undefined} />
                      <CheeseStatusBadge statuses={cheeseStatus[p.art]} />
                    </td>
                    <td className="an-num">{fmt(p.sales)}</td>
                    <td className="an-num an-ret">{fmt(p.returns)}</td>
                    <td className={`an-num an-pct${p.return_rate > 30 ? ' an-bad' : p.return_rate > 15 ? ' an-warn' : ''}`}>
                      {pct(p.return_rate)}
                    </td>
                    <td>
                      <button className="an-forecast-btn" onClick={() => onForecast(p.art)} title="Прогноз">📈</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {worstReturns.length > 0 && (
        <Section title="Высокий процент возвратов" stale={top.stale}>
          <ResponsiveContainer width="100%" height={Math.max(220, worstReturns.length * 28)}>
            <BarChart data={worstReturns} layout="vertical" margin={{ left: 0, right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#666' }} unit="%" domain={[0, 'dataMax + 5']} />
              <YAxis type="category" dataKey="art" tick={{ fontSize: 12, fill: 'var(--lg-text-3)' }} width={90} />
              <Tooltip {...TOOLTIP} formatter={(v, _n, p) => [`${v}%  (${fmt(p.payload.sales)} прод.)`, 'Возврат']} />
              <Bar dataKey="return_rate" name="% возвратов" radius={[0,4,4,0]}>
                {worstReturns.map((p) => (
                  <rect key={p.art}
                    fill={p.return_rate > 40 ? '#ff4444' : p.return_rate > 25 ? '#ffe66d' : '#4ecdc4'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Поставщики
// ---------------------------------------------------------------------------

const URGENCY_CONFIG = {
  critical: { label: '🔴 Срочно',   cls: 'cat-urgent'  },
  soon:     { label: '🟡 Скоро',    cls: 'cat-buynow'  },
  plan:     { label: '🟢 Планово',  cls: 'cat-stable'  },
};

const PRICE_DOWN_SIGNAL = {
  returns:    { label: '📦 Возвраты',    cls: 'cat-monitor' },
  overstock:  { label: '📉 Зависание',   cls: 'cat-monitor' },
  declining:  { label: '↘ Спад спроса', cls: 'cat-monitor' },
  wait_season:{ label: '🌙 Ждать сезон', cls: 'cat-offseason' },
  cheapest:   { label: '✓ Мин. цена',   cls: 'cat-stable' },
};

function SupplierDetailPanel({ supplier, onClose, onReport }) {
  const { data, loading, error } = useCached(
    () => analyticsAPI.getSupplierDetail(supplier),
    `supplier_detail:${supplier}`,
  );

  const [activeSection, setActiveSection] = useState('restock');

  // Журнал продаж — загружается лениво при первом открытии таба
  const [salesLog,        setSalesLog]        = useState(null);
  const [salesLogLoading, setSalesLogLoading] = useState(false);
  const [salesLogError,   setSalesLogError]   = useState(false);
  const [salesLogDays,    setSalesLogDays]    = useState(90);

  useEffect(() => {
    if (activeSection !== 'log') return;
    setSalesLog(null);
    setSalesLogLoading(true);
    setSalesLogError(false);
    analyticsAPI.getSupplierSalesLog(supplier, { days: salesLogDays })
      .then(r => setSalesLog(r.data))
      .catch(() => setSalesLogError(true))
      .finally(() => setSalesLogLoading(false));
  }, [activeSection, supplier, salesLogDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const summary    = data?.summary    || {};
  const restock    = data?.restock    || [];
  const price_up   = data?.price_up   || [];
  const price_down = data?.price_down || [];
  const sales      = data?.sales      || [];

  const restockSkus   = restock.map(r => r.art);
  const cheeseStatus  = useCheeseStatus(restockSkus);

  const [salesSearch, setSalesSearch] = useState('');
  const [salesSort, setSalesSort] = useState({ key: 'v30', dir: 'desc' });

  const handleSalesSort = (key) => {
    setSalesSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' }
    );
  };

  const salesFiltered = (() => {
    let list = salesSearch
      ? sales.filter(s => s.art.toLowerCase().includes(salesSearch.toLowerCase()) || s.name.toLowerCase().includes(salesSearch.toLowerCase()))
      : sales;
    const { key, dir } = salesSort;
    return [...list].sort((a, b) => dir === 'desc' ? b[key] - a[key] : a[key] - b[key]);
  })();

  const sections = [
    { key: 'sales',      label: `🛍 Продажи (${sales.length})` },
    { key: 'restock',    label: `📦 Пополнить (${restock.length})` },
    { key: 'price_up',  label: `📈 Поднять цену (${price_up.length})` },
    { key: 'price_down',label: `🏷 Снизить / распродать (${price_down.length})` },
    { key: 'log',        label: '📋 Журнал продаж' },
  ];

  return (
    <div className="an-modal-overlay" onClick={onClose}>
      <div className="an-supplier-modal" onClick={e => e.stopPropagation()}>
        <div className="an-supplier-detail-head">
          <div>
            <span className="an-supplier-name">{supplier}</span>
            {data && (
              <span className="an-supplier-meta">
                &nbsp;· 7д: <b>{fmt(summary.v7)}</b>&ensp;
                30д: <b>{fmt(summary.v30)}</b>&ensp;
                Возвраты: <b className={summary.return_rate > 25 ? 'an-bad' : 'an-ok'}>{pct(summary.return_rate)}</b>&ensp;
                Моделей 30д: <b>{fmt(summary.models)}</b>&ensp;
                Всего: <b>{fmt(summary.models_all)}</b>
              </span>
            )}
          </div>
          <button className="an-modal-close" onClick={onClose} title="Закрыть (Esc)">✕</button>
        </div>

        {loading && <Loader />}
        {error   && <ErrBox />}

        {data && (
          <>
            <div className="an-tabs" style={{ marginBottom: 14 }}>
              {sections.map(s => (
                <button key={s.key}
                  className={`an-tab-btn${activeSection === s.key ? ' active' : ''}`}
                  onClick={() => setActiveSection(s.key)}
                >{s.label}</button>
              ))}
            </div>

            {activeSection === 'sales' && (
              sales.length === 0
                ? <div className="an-error" style={{ padding: '20px 0' }}>Нет товаров</div>
                : <>
                    <input
                      className="an-sales-search"
                      placeholder="Поиск по артикулу или названию…"
                      value={salesSearch}
                      onChange={e => setSalesSearch(e.target.value)}
                    />
                    <div className="an-sales-grid">
                      {salesFiltered.map(s => (
                        <div key={s.art} className={`an-sales-tile${onReport ? ' an-sales-tile--clickable' : ''}`} onClick={onReport ? () => onReport(s.art) : undefined}>
                          <div className="an-sales-tile-photo">
                            {s.photo
                              ? <img src={proxyImageUrl(s.photo)} alt="" loading="lazy" />
                              : <div className="an-sales-tile-nophoto" />}
                          </div>
                          <div className="an-sales-tile-art" title={s.art}>{s.art}</div>
                          <div className="an-sales-tile-name" title={s.name}>{s.name}</div>
                          <div className="an-sales-tile-stats">
                            {[
                              { k: 'v30',         lbl: '30д',    val: s.v30,                                         cls: '' },
                              { k: 'v_today',     lbl: 'сег.',   val: s.v_today,                                     cls: s.v_today > 0 ? ' an-sales-today' : '' },
                              { k: 'return_rate', lbl: 'возвр.', val: `${s.return_rate}%`,                           cls: s.return_rate > 30 ? ' an-bad' : s.return_rate > 15 ? ' an-warn' : '' },
                              { k: 'stock',       lbl: 'сток',   val: s.stock,                                       cls: '' },
                            ].map(({ k, lbl, val, cls }) => (
                              <span key={k} className="an-sales-stat an-sales-stat--sort" onClick={e => { e.stopPropagation(); handleSalesSort(k); }} title={`Сортировать по «${lbl}»`}>
                                <span className={`an-sales-stat-lbl${salesSort.key === k ? ' an-sales-sort-active' : ''}`}>
                                  {lbl}{salesSort.key === k ? (salesSort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                                </span>
                                <span className={`an-sales-stat-val${cls}`}>{val}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
            )}

            {activeSection === 'restock' && (
              restock.length === 0
                ? <div className="an-error" style={{ padding: '20px 0' }}>Нет позиций, требующих пополнения</div>
                : <div className="an-table-wrap an-table-wrap--modal">
                    <table className="an-table">
                      <thead>
                        <tr><th>#</th><th colSpan={2}>Товар</th><th>Срочность</th><th>7д</th><th>Прод/д</th><th>Наличие</th><th>Сток</th><th>Дней</th><th>Возвр.</th></tr>
                      </thead>
                      <tbody>
                        {restock.map((r, i) => {
                          const effRate = r.forecast_daily ?? r.daily_avg;
                          const isAdj = r.availability_pct != null && r.availability_pct < 83 && effRate !== r.daily_avg;
                          return (
                          <tr key={r.art} className={r.urgency === 'critical' ? 'an-row-danger' : r.urgency === 'soon' ? 'an-row-warn' : ''}>
                            <td className="an-rank">{i + 1}</td>
                            <td colSpan={2}>
                              <ProductCell photo={r.photo} art={r.art} name={r.name} onClick={onReport ? () => onReport(r.art) : undefined} />
                              <CheeseStatusBadge statuses={cheeseStatus[r.art]} />
                            </td>
                            <td><span className={`an-cat-badge ${URGENCY_CONFIG[r.urgency]?.cls}`}>{URGENCY_CONFIG[r.urgency]?.label}</span></td>
                            <td className="an-num"><b>{fmt(r.v7)}</b></td>
                            <td className="an-num" title={isAdj ? `Скорр. с ${r.daily_avg} → ${effRate} (наличие ${r.availability_pct}%)` : undefined}>
                              {isAdj ? <><span style={{textDecoration:'line-through',opacity:.5,fontSize:'11px'}}>{r.daily_avg}</span> <b style={{color:'#d97706'}}>{effRate}</b></> : r.daily_avg}
                            </td>
                            <td className={`an-num${r.availability_pct != null && r.availability_pct < 70 ? ' an-warn' : ''}`}>
                              {r.availability_pct != null ? `${r.availability_pct}%` : '—'}
                            </td>
                            <td className="an-num">{fmt(r.stock)}</td>
                            <td className={`an-days${r.days_left <= 5 ? ' an-bad' : r.days_left <= 14 ? ' an-warn' : ''}`}>
                              {r.days_left >= 999 ? '∞' : `${r.days_left}д`}
                            </td>
                            <td className={`an-num an-pct${r.return_rate > 25 ? ' an-warn' : ' an-ok'}`}>{pct(r.return_rate)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
            )}

            {activeSection === 'price_up' && (
              price_up.length === 0
                ? <div className="an-error" style={{ padding: '20px 0' }}>Нет позиций для повышения цены</div>
                : <div className="an-table-wrap an-table-wrap--modal">
                    <table className="an-table">
                      <thead>
                        <tr><th>#</th><th colSpan={2}>Товар</th><th>7д</th><th>Тренд</th><th>Сток</th><th>Дней</th><th>Возвр.</th><th>Рекомендация</th></tr>
                      </thead>
                      <tbody>
                        {price_up.map((r, i) => (
                          <tr key={r.art} className="an-row-warn">
                            <td className="an-rank">{i + 1}</td>
                            <td colSpan={2}><ProductCell photo={r.photo} art={r.art} name={r.name} onClick={onReport ? () => onReport(r.art) : undefined} /></td>
                            <td className="an-num"><b>{fmt(r.v7)}</b></td>
                            <td><TrendArrow pct={r.trend_pct} /></td>
                            <td className="an-num">{fmt(r.stock)}</td>
                            <td className={`an-days${r.days_left <= 5 ? ' an-bad' : r.days_left <= 14 ? ' an-warn' : ''}`}>
                              {r.days_left >= 999 ? '∞' : `${r.days_left}д`}
                            </td>
                            <td className={`an-num an-pct${r.return_rate > 20 ? ' an-warn' : ' an-ok'}`}>{pct(r.return_rate)}</td>
                            <td className="an-price-reason">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
            )}

            {activeSection === 'log' && (() => {
              const LOG_SIZES = [36,37,38,39,40,41,42,43,44,45,46];
              const fmtDate = (d) => {
                if (!d) return d;
                const s = String(d).slice(0,10);
                const [y,m,dd] = s.split('-');
                return `${parseInt(dd)}.${parseInt(m)}.${y}`;
              };
              // group by date
              const grouped = {};
              (salesLog || []).forEach(r => {
                const k = String(r.DataTime).slice(0,10);
                if (!grouped[k]) grouped[k] = [];
                grouped[k].push(r);
              });
              const dates = Object.keys(grouped).sort((a,b) => b.localeCompare(a));
              return (
                <div>
                  <div className="an-log-toolbar">
                    <span className="an-log-toolbar-label">Период:</span>
                    {[30,60,90,180].map(d => (
                      <button key={d}
                        className={`an-log-day-btn${salesLogDays === d ? ' active' : ''}`}
                        onClick={() => setSalesLogDays(d)}
                      >{d}д</button>
                    ))}
                    {salesLog && <span className="an-log-count">{salesLog.length} записей</span>}
                  </div>
                  {salesLogLoading && <Loader />}
                  {salesLogError  && <ErrBox />}
                  {salesLog && !salesLogLoading && (
                    salesLog.length === 0
                      ? <div className="an-error" style={{ padding: '20px 0' }}>Нет данных за выбранный период</div>
                      : <div className="an-table-wrap an-table-wrap--modal an-log-wrap">
                          <table className="an-table an-log-table">
                            <thead>
                              <tr>
                                <th className="an-log-th-date">Дата</th>
                                <th>id</th>
                                <th>Артикул ALL</th>
                                <th>Артикул POST</th>
                                {LOG_SIZES.map(s => <th key={s} className="an-log-th-size">{s}</th>)}
                                <th className="an-log-th-all">Всего</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dates.map(date => {
                                const rows = grouped[date];
                                const dayTotal = rows.reduce((s,r) => s + (r.SIZEALL||0), 0);
                                return (
                                  <React.Fragment key={date}>
                                    <tr className="an-log-date-row">
                                      <td colSpan={4 + LOG_SIZES.length + 1}>
                                        <span className="an-log-date-label">{fmtDate(date)}</span>
                                        <span className="an-log-date-count">{dayTotal}</span>
                                      </td>
                                    </tr>
                                    {rows.map(r => (
                                      <tr
                                        key={r.id}
                                        className={`an-log-row${onReport ? ' an-log-row--clickable' : ''}`}
                                        onClick={onReport ? () => onReport(r.artALL) : undefined}
                                        title={onReport ? `Открыть отчёт: ${r.artALL}` : undefined}
                                      >
                                        <td className="an-log-td-date an-muted">{fmtDate(r.DataTime)}</td>
                                        <td className="an-num an-muted">{r.id}</td>
                                        <td className="an-log-art">{r.artALL}</td>
                                        <td className="an-muted">{r.artPOST || '—'}</td>
                                        {LOG_SIZES.map(s => (
                                          <td key={s} className="an-log-size-cell">
                                            {r[`SIZE${s}`] > 0 ? r[`SIZE${s}`] : ''}
                                          </td>
                                        ))}
                                        <td className="an-log-th-all an-num"><b>{r.SIZEALL || 0}</b></td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                  )}
                </div>
              );
            })()}

            {activeSection === 'price_down' && (
              price_down.length === 0
                ? <div className="an-error" style={{ padding: '20px 0' }}>Нет позиций для снижения цены</div>
                : <div className="an-table-wrap an-table-wrap--modal">
                    <table className="an-table">
                      <thead>
                        <tr>
                          <th>#</th><th colSpan={2}>Товар</th><th>Сигнал</th>
                          <th>Цена</th><th>Рынок мин</th><th>Рынок avg</th>
                          <th>7д</th><th>Прод/д</th><th>Сток</th><th>Возвр.</th>
                          <th>Причина</th>
                        </tr>
                      </thead>
                      <tbody>
                        {price_down.map((r, i) => {
                          const sig = PRICE_DOWN_SIGNAL[r.signal] || { label: r.signal, cls: 'cat-monitor' };
                          const isWait    = r.signal === 'wait_season';
                          const isCheap   = r.signal === 'cheapest';
                          const rowCls    = isWait ? 'an-row-info' : isCheap ? '' : 'an-row-avoid';
                          const priceGap  = r.sup_price > 0 && r.market_min > 0
                            ? r.sup_price - r.market_min : null;
                          return (
                            <tr key={r.art} className={rowCls}>
                              <td className="an-rank">{i + 1}</td>
                              <td colSpan={2}><ProductCell photo={r.photo} art={r.art} name={r.name} onClick={onReport ? () => onReport(r.art) : undefined} /></td>
                              <td><span className={`an-cat-badge ${sig.cls}`}>{sig.label}</span></td>
                              <td className="an-num an-price-cell">
                                {r.sup_price > 0 ? `${r.sup_price}грн` : '—'}
                              </td>
                              <td className={`an-num${priceGap > 0 ? ' an-warn' : priceGap < 0 ? ' an-ok' : ''}`}>
                                {r.market_min > 0 ? `${r.market_min}грн` : '—'}
                              </td>
                              <td className="an-num an-muted">
                                {r.market_avg > 0 ? `${r.market_avg}грн` : '—'}
                              </td>
                              <td className="an-num">{fmt(r.v7)}</td>
                              <td className="an-num">{r.daily_avg}</td>
                              <td className="an-num">{fmt(r.stock)}</td>
                              <td className={`an-num an-pct${r.return_rate > 35 ? ' an-bad' : r.return_rate > 20 ? ' an-warn' : ''}`}>{pct(r.return_rate)}</td>
                              <td className="an-price-reason">{r.reason}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SuppliersTab({ onReport }) {
  const [period,   setPeriod]  = useState(30);
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]  = useState('');
  const [sortKey,  setSortKey]  = useState('sales');
  const [sortDir,  setSortDir]  = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const { data, loading, stale, error } = useCached(
    () => analyticsAPI.getSuppliers({ period }),
    `suppliers:${period}`,
  );

  const filtered = data
    ? (search.trim()
        ? data.filter(s => s.supplier.toLowerCase().includes(search.trim().toLowerCase()))
        : data)
    : [];

  const sorted = [...filtered].sort((a, b) => {
    const av = sortKey === 'supplier' ? a.supplier : (a[sortKey] ?? 0);
    const bv = sortKey === 'supplier' ? b.supplier : (b[sortKey] ?? 0);
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const SortTh = ({ label, k, title }) => (
    <th title={title} onClick={() => handleSort(k)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label}{sortKey === k ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕'}
    </th>
  );

  const chartData = filtered.slice(0, 15);

  return (
    <div>
      <Section title="Продажи по поставщикам" stale={stale}
        action={<PeriodPicker value={period} onChange={setPeriod} options={PERIOD_OPTS} />}
      >
        {loading && <Loader />}
        {error   && <ErrBox />}
        {data && (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#666' }} />
                <YAxis type="category" dataKey="supplier" tick={{ fontSize: 12, fill: 'var(--lg-text-3)' }} width={110} />
                <Tooltip {...TOOLTIP} />
                <Legend wrapperStyle={{ color: 'var(--lg-text-2)', fontSize: 13 }} />
                <Bar dataKey="sales"   name="Продажи"  fill="#4f8cff" radius={[0,3,3,0]} />
                <Bar dataKey="returns" name="Возвраты" fill="#ff6b6b" radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>

            <div className="an-supplier-search-row">
              <input
                className="an-supplier-search"
                placeholder="Поиск поставщика..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <span className="an-supplier-search-count">
                  {filtered.length} из {data.length}
                </span>
              )}
            </div>

            <div className="an-table-wrap" style={{ marginTop: 8 }}>
              <table className="an-table" style={{ tableLayout: 'fixed', width: 'auto', minWidth: 740 }}>
                <colgroup>
                  <col style={{ width: 36 }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 80 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <SortTh label="Поставщик"    k="supplier" />
                    <SortTh label="Продажи"      k="sales" />
                    <SortTh label="Сегодня"      k="sales_today" title="Продажи сегодня" />
                    <SortTh label="Возвраты"     k="returns" />
                    <SortTh label="% возв."      k="return_rate" />
                    <SortTh label="Моделей 30д"  k="models" title="Уникальных моделей за выбранный период" />
                    <SortTh label="Всего"        k="models_all" title="Уникальных моделей за всё время" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s, i) => (
                    <tr key={s.supplier}
                      className={`an-supplier-row${selected === s.supplier ? ' an-supplier-row--active' : ''}`}
                      onClick={() => setSelected(selected === s.supplier ? null : s.supplier)}
                      title="Нажмите для детальной аналитики"
                    >
                      <td className="an-rank">{i + 1}</td>
                      <td className="an-name-cell an-supplier-link">{s.supplier}</td>
                      <td className="an-num">{fmt(s.sales)}</td>
                      <td className="an-num an-today">{s.sales_today > 0 ? fmt(s.sales_today) : '—'}</td>
                      <td className="an-num an-ret">{fmt(s.returns)}</td>
                      <td className={`an-num an-pct${s.return_rate > 30 ? ' an-bad' : s.return_rate > 15 ? ' an-warn' : ''}`}>
                        {pct(s.return_rate)}
                      </td>
                      <td className="an-num">{fmt(s.models)}</td>
                      <td className="an-num an-muted">{fmt(s.models_all)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Section>

      {selected && (
        <SupplierDetailPanel
          supplier={selected}
          onClose={() => setSelected(null)}
          onReport={onReport}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Size distribution modal
// ---------------------------------------------------------------------------

const SIZE_PERIOD_OPTS = [
  { value: 'week',  label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'all',   label: 'Всё время' },
];

const SIGNAL_STYLE = {
  critical:    { bg: '#3a0808', border: '#7a1a1a', label: '🔴 Дефицит везде' },
  restock_sup: { bg: '#3a2000', border: '#7a4400', label: '🟡 Нет у Cheese'  },
  watch:       { bg: '#1a1a00', border: '#3a3a00', label: '📉 Тает'           },
  no_sales:    { bg: '#1a1a2e', border: '#2a2a3a', label: '—'                 },
};

function SizeModal({ art, name, onClose, supplier = 'Cheese' }) {
  const [period,     setPeriod]     = useState('month');
  const [targetDays, setTargetDays] = useState(30);
  const [orderQty,   setOrderQty]   = useState(0);  // 0 = auto from deficit

  // Smart data with stock awareness
  const { data, loading, error } = useCached(
    () => analyticsAPI.getSizeOrder(art, { supplier, period, target_days: targetDays }),
    `size_order:${art}:${supplier}:${period}:${targetDays}`,
  );

  // Compute recommended qty per size
  const sizes = data?.sizes || [];
  const totalDeficit = data?.total_deficit || 0;

  const recommend = sizes.map(s => {
    let qty;
    const qty0 = orderQty > 0 ? orderQty : totalDeficit;
    if (totalDeficit > 0) {
      // Deficit-weighted: sizes running out get proportionally more
      qty = qty0 > 0 ? Math.round(qty0 * s.deficit / totalDeficit) : 0;
    } else {
      // No deficit — distribute by historical %
      qty = qty0 > 0 ? Math.max(Math.round(qty0 * s.pct / 100), s.pct > 3 ? 1 : 0) : 0;
    }
    return { ...s, qty };
  });

  const recTotal = recommend.reduce((a, b) => a + b.qty, 0);
  const maxSales = Math.max(...sizes.map(s => s.sales), 1);
  const maxStock = Math.max(...sizes.map(s => Math.max(s.stock_sup, s.stock_all)), 1);

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="an-modal-overlay" onClick={onClose}>
      <div className="an-modal an-modal--sizes" onClick={e => e.stopPropagation()}>
        <div className="an-modal-head">
          <span>📐 Размеры — {name}</span>
          <button className="an-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="an-size-controls">
          <PeriodPicker value={period} onChange={setPeriod} options={SIZE_PERIOD_OPTS} />
          <div className="an-size-qty-wrap">
            <span className="an-size-qty-label">Покрытие</span>
            <input className="an-size-qty-input" type="number" min={7} max={90} step={7}
              value={targetDays} onChange={e => setTargetDays(Number(e.target.value))} />
            <span className="an-size-qty-label">дней</span>
          </div>
          <div className="an-size-qty-wrap">
            <span className="an-size-qty-label">Взять пар</span>
            <input className="an-size-qty-input" type="number" min={0} max={999}
              value={orderQty} onChange={e => setOrderQty(Number(e.target.value))}
              placeholder="авто" />
          </div>
        </div>

        {loading && <Loader />}
        {error   && <ErrBox />}

        {data && sizes.length === 0 && (
          <div className="an-error">Нет данных по размерам</div>
        )}

        {data && sizes.length > 0 && (
          <>
            {/* Legend */}
            <div className="an-size-legend">
              <span className="an-size-legend-item"><span className="an-size-leg-bar an-size-leg-sales"/>Продажи</span>
              <span className="an-size-legend-item"><span className="an-size-leg-bar an-size-leg-sup"/>Сток {supplier}</span>
              <span className="an-size-legend-item"><span className="an-size-leg-bar an-size-leg-all"/>Сток всего</span>
            </div>

            {/* Per-size bars */}
            <div className="an-size-grid an-size-grid--smart">
              {recommend.map(s => {
                const sig = SIGNAL_STYLE[s.signal] || {};
                const isCritical = s.signal === 'critical' || s.signal === 'restock_sup';
                return (
                  <div key={s.size} className="an-size-cell an-size-cell--smart"
                    style={sig.bg ? { background: sig.bg, borderColor: sig.border, borderWidth: 1, borderStyle: 'solid' } : {}}>
                    {/* Size label */}
                    <div className="an-size-num" style={{ fontWeight: isCritical ? 700 : 400 }}>{s.size}</div>

                    {/* Three bars side-by-side */}
                    <div className="an-size-bars">
                      {/* Sales bar */}
                      <div className="an-size-bar-col" title={`Продаж: ${s.sales}`}>
                        <div className="an-size-bar-track">
                          <div className="an-size-bar an-size-bar--sales"
                            style={{ height: `${Math.max(s.sales / maxSales * 56, 2)}px` }} />
                        </div>
                      </div>
                      {/* Stock Cheese bar */}
                      <div className="an-size-bar-col" title={`Сток ${supplier}: ${s.stock_sup}`}>
                        <div className="an-size-bar-track">
                          <div className="an-size-bar an-size-bar--sup"
                            style={{ height: `${Math.max(s.stock_sup / maxStock * 56, s.stock_sup > 0 ? 2 : 0)}px` }} />
                        </div>
                      </div>
                      {/* Stock all bar */}
                      <div className="an-size-bar-col" title={`Сток все: ${s.stock_all}`}>
                        <div className="an-size-bar-track">
                          <div className="an-size-bar an-size-bar--all"
                            style={{ height: `${Math.max(s.stock_all / maxStock * 56, s.stock_all > 0 ? 2 : 0)}px` }} />
                        </div>
                      </div>
                    </div>

                    {/* % and coverage */}
                    <div className="an-size-pct">{s.pct > 0 ? `${s.pct}%` : '—'}</div>
                    <div className="an-size-cov" title={`${supplier}: ${s.days_sup}д / Все: ${s.days_all}д`}>
                      <span className={s.days_sup <= 7 ? 'an-bad' : s.days_sup <= 14 ? 'an-warn' : 'an-ok'}>
                        {s.days_sup >= 999 ? '∞' : `${s.days_sup}д`}
                      </span>
                    </div>

                    {/* Recommended order */}
                    <div className={`an-size-qty${s.deficit > 0 ? ' an-size-qty--needed' : ''}`}>
                      {s.qty > 0 ? `${s.qty}п` : '—'}
                    </div>

                    {/* Signal badge */}
                    {sig.label && sig.label !== '—' && (
                      <div className="an-size-signal">{sig.label}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="an-size-summary">
              {totalDeficit > 0
                ? <>Дефицит Cheese: <b>{totalDeficit}</b> пар чтобы покрыть {targetDays} дней продаж</>
                : <>Сток у {supplier} достаточен на {targetDays} дней по всем размерам ✓</>
              }
            </div>
            {recTotal > 0 && (
              <div className="an-size-summary" style={{ marginTop: 4 }}>
                Рекомендую взять: <b>{recTotal}</b> пар
                {orderQty > 0 && recTotal !== orderQty && (
                  <span className="an-size-diff"> (округление от {orderQty})</span>
                )}
              </div>
            )}
            <div className="an-size-note">
              Данные по продажам за период · Сток: ACTUALDATA на сейчас
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category config
// ---------------------------------------------------------------------------

const CAT_CONFIG = {
  urgent:     { label: '🔴 Срочно',      cls: 'cat-urgent',     tip: 'Сток закончится ДО прихода товара (< 35 дней) — заказывай немедленно' },
  buy_now:    { label: '🟠 Взять',       cls: 'cat-buynow',     tip: 'Сток < 50 дней — нужно заказывать, учитывая 35 дней доставки' },
  novelty:    { label: '🆕 Новинка',     cls: 'cat-novelty',    tip: 'Новый товар с сильным стартом' },
  trending:   { label: '📈 Растёт',      cls: 'cat-trend',      tip: 'Рост продаж > +40% vs прошлой недели' },
  stable:     { label: '✅ Хит',         cls: 'cat-stable',     tip: 'Продаётся каждый день, низкий возврат' },
  monitor:    { label: '📊 Мониторинг',  cls: 'cat-monitor',    tip: 'Продаётся, но без особых сигналов' },
  off_season: { label: '🌙 Не сезон',    cls: 'cat-offseason',  tip: 'Исторически слабые продажи в ближайшие 2 месяца — не спешить с закупкой' },
  avoid:      { label: '⛔ Не брать',    cls: 'cat-avoid',      tip: 'Высокий % возвратов или падающий спрос' },
};

const ALL_CATS = Object.keys(CAT_CONFIG);

function TrendArrow({ pct }) {
  if (pct > 20)  return <span className="an-trend-up">▲ +{pct}%</span>;
  if (pct < -20) return <span className="an-trend-dn">▼ {pct}%</span>;
  return <span className="an-trend-fl">→ {pct > 0 ? '+' : ''}{pct}%</span>;
}

// ---------------------------------------------------------------------------
// Tab: Упущенные хиты
// ---------------------------------------------------------------------------

function LostTab({ onReport }) {
  const [minDailyAvg, setMinDailyAvg] = useState(0.3);
  const [minDaysOos,  setMinDaysOos]  = useState(7);

  const { data, loading, error, stale } = useCached(
    () => analyticsAPI.getLostOpportunities({ min_daily_avg: minDailyAvg, min_days_oos: minDaysOos, limit: 60 }),
    `lost_opp:${minDailyAvg}:${minDaysOos}`,
  );

  const items = data || [];

  // Group by how long they've been out
  const freshlyOut  = items.filter(i => i.days_since_last_sale <= 21);
  const longOut     = items.filter(i => i.days_since_last_sale > 21 && i.days_since_last_sale <= 60);
  const veryLongOut = items.filter(i => i.days_since_last_sale > 60);

  function LostTable({ rows, emptyText }) {
    if (!rows || rows.length === 0) return <div className="an-empty">{emptyText}</div>;
    return (
      <div className="an-table-wrap">
        <table className="an-table">
          <thead>
            <tr>
              <th>Товар</th>
              <th title="Средние продажи в день за 90 дней до исчезновения">Темп/д</th>
              <th title="Активных дней продаж за 90 дней">Акт.дн.</th>
              <th title="Продаж за 90 дней">Прод. 90д</th>
              <th title="Последняя продажа">Посл. продажа</th>
              <th title="Дней без стока">Без стока</th>
              <th title="Оценка упущенной возможности: темп × √(дней OOS)">Потенциал</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.art}
                className={onReport ? 'an-sales-tile--clickable' : ''}
                onClick={onReport ? () => onReport(r.art) : undefined}
                title={onReport ? 'Открыть полный отчёт по товару' : ''}
              >
                <td>
                  <div className="an-product-row">
                    {r.photo
                      ? <img className="an-thumb" src={proxyImageUrl(r.photo)} alt="" />
                      : <div className="an-thumb an-thumb--empty" />
                    }
                    <div className="an-product-info">
                      <span className="an-art-cell">{r.art}</span>
                      <span className="an-name-cell">{r.name}</span>
                    </div>
                  </div>
                </td>
                <td className="an-num an-today">{r.daily_avg_90d}</td>
                <td className="an-num">{r.active_days_90d}д</td>
                <td className="an-num"><b>{fmt(r.sales_90d)}</b></td>
                <td className="an-num an-lost-date">{r.last_sale_date || '—'}</td>
                <td className={`an-num ${r.days_since_last_sale > 60 ? 'an-bad' : r.days_since_last_sale > 21 ? 'an-warn' : 'an-ok'}`}>
                  {r.days_since_last_sale}д
                </td>
                <td className="an-num an-lost-score">{Math.round(r.opportunity_score)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      {/* Explanation */}
      <div className="an-lost-header">
        <div className="an-lost-header-text">
          <b>Упущенные хиты</b> — товары, которых нет в наличии, но которые хорошо продавались.
          Они невидимы в других разделах, хотя рынок уже подтвердил спрос.
        </div>
        <div className="an-lost-header-stats">
          <span className="an-lost-stat"><b>{items.length}</b> товаров</span>
          <span className="an-lost-stat-sep">·</span>
          <span className="an-lost-stat an-ok"><b>{freshlyOut.length}</b> пропали недавно</span>
          <span className="an-lost-stat-sep">·</span>
          <span className="an-lost-stat an-warn"><b>{longOut.length}</b> более 3 недель</span>
          <span className="an-lost-stat-sep">·</span>
          <span className="an-lost-stat an-bad"><b>{veryLongOut.length}</b> более 2 месяцев</span>
        </div>
      </div>

      {/* Filters */}
      <div className="an-lost-filters">
        <span className="an-verdict-order-label">Мин. темп:</span>
        {[0.1, 0.3, 0.5, 1.0].map(v => (
          <button key={v}
            className={`an-verdict-days-btn${minDailyAvg === v ? ' an-verdict-days-btn--active' : ''}`}
            onClick={() => setMinDailyAvg(v)}
          >{v}/д</button>
        ))}
        <span className="an-verdict-order-sep an-lost-sep">·</span>
        <span className="an-verdict-order-label">OOS минимум:</span>
        {[3, 7, 14, 30].map(d => (
          <button key={d}
            className={`an-verdict-days-btn${minDaysOos === d ? ' an-verdict-days-btn--active' : ''}`}
            onClick={() => setMinDaysOos(d)}
          >{d}д</button>
        ))}
      </div>

      {loading && <Loader />}
      {error   && <ErrBox />}

      {!loading && !error && items.length === 0 && (
        <div className="an-empty">Нет товаров с такими параметрами — отличная работа! 🎉</div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          {freshlyOut.length > 0 && (
            <Section title={`🟡 Закончились недавно (до 21 дня) — ${freshlyOut.length} товаров`} stale={stale}>
              <div className="an-alert an-alert--warn an-lost-tip">
                Скорее всего ещё актуальны — спрос не остыл. Высокий приоритет для дозаказа.
              </div>
              <LostTable rows={freshlyOut} />
            </Section>
          )}

          {longOut.length > 0 && (
            <Section title={`🟠 Долго без стока (22–60 дней) — ${longOut.length} товаров`} stale={stale}>
              <div className="an-alert an-alert--info an-lost-tip">
                Возможна потеря позиций в поиске у маркетплейсов. Проверьте, ещё ли актуален товар.
              </div>
              <LostTable rows={longOut} />
            </Section>
          )}

          {veryLongOut.length > 0 && (
            <Section title={`🔴 Пропали давно (более 60 дней) — ${veryLongOut.length} товаров`} stale={stale}>
              <div className="an-alert an-alert--danger an-lost-tip">
                Высокий риск потери рынка. Возможно сезонность или снятие с производства — проверьте у поставщика.
              </div>
              <LostTable rows={veryLongOut} />
            </Section>
          )}
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Tab: Закупки
// ---------------------------------------------------------------------------

function RestockTab({ onForecast, onReport }) {
  const [catFilter,    setCatFilter]    = useState('all');
  const [sizesArt,     setSizesArt]     = useState(null);
  const [searchQuery,  setSearchQuery]  = useState('');

  const intel = useCached(
    () => analyticsAPI.getPurchaseIntelligence({ limit: 500 }),
    'purchase_intel:500',
  );

  const novelty = useCached(
    () => analyticsAPI.getNoveltyProducts({ limit: 500 }),
    'novelty_products:500',
  );

  const evergreens = useCached(
    () => analyticsAPI.getEvergreens({ limit: 40 }),
    'evergreens:40',
  );

  const intelSkus = intel.data ? intel.data.map(r => r.art) : [];
  const cheeseStatus = useCheeseStatus(intelSkus);

  const LEAD_TIME = 35; // should match backend LEAD_TIME_DAYS

  const items = intel.data || [];
  const counts = ALL_CATS.reduce((acc, c) => {
    acc[c] = c === 'novelty'
      ? (novelty.data || []).length
      : items.filter(i => i.category === c).length;
    return acc;
  }, {});

  const urgent      = items.filter(i => i.category === 'urgent').length;
  const buy_now     = items.filter(i => i.category === 'buy_now').length;
  const off_season  = items.filter(i => i.category === 'off_season').length;
  const promo_count = items.filter(i => i.price_promo_likely).length;

  const isNoveltyTab = catFilter === 'novelty';

  const filtered = (() => {
    const q = searchQuery.trim().toLowerCase();
    let list;
    if (isNoveltyTab) {
      list = novelty.data || [];
    } else if (catFilter === 'all') {
      list = items.filter(i => i.category !== 'avoid');
    } else {
      list = items.filter(i => i.category === catFilter);
    }
    if (q) {
      list = list.filter(i =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.art  || '').toLowerCase().includes(q)
      );
    }
    return list;
  })();

  return (
    <div>
      {sizesArt && (
        <SizeModal art={sizesArt.art} name={sizesArt.name} onClose={() => setSizesArt(null)} />
      )}

      {/* Alerts */}
      {(urgent > 0 || buy_now > 0 || off_season > 0 || promo_count > 0) && (
        <div className="an-alert-row">
          {urgent      > 0 && <div className="an-alert an-alert--danger">🔴 Заказать немедленно: {urgent} товаров — сток закончится до прихода ({LEAD_TIME}д)</div>}
          {buy_now     > 0 && <div className="an-alert an-alert--warn">🟠 Скоро заказывать: {buy_now} товаров (сток {'<'} {LEAD_TIME + 14} дней)</div>}
          {off_season  > 0 && <div className="an-alert an-alert--info">🌙 Не сезон: {off_season} товаров — в ближайшие месяцы исторически слабые продажи</div>}
          {promo_count > 0 && <div className="an-alert an-alert--promo">⚡ Возможная акция: {promo_count} товаров с резким ростом продаж — спрос может быть искусственным, не завышайте закупку</div>}
        </div>
      )}

      {/* Purchase Intelligence */}
      <Section title="Умные рекомендации по закупке" stale={intel.stale}>
        {/* Category filter */}
        <div className="an-cat-filters">
          <button className={`an-cat-btn${catFilter === 'all' ? ' active' : ''}`} onClick={() => setCatFilter('all')}>
            Все активные
          </button>
          {ALL_CATS.map(c => (
            <button key={c}
              className={`an-cat-btn an-cat-btn--${c}${catFilter === c ? ' active' : ''}`}
              onClick={() => setCatFilter(c)}
              title={CAT_CONFIG[c].tip}
            >
              {CAT_CONFIG[c].label}
              {counts[c] > 0 && <span className="an-cat-count">{counts[c]}</span>}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ margin: '10px 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            placeholder="🔍 Пошук за назвою або артикулом…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              padding: '6px 12px', borderRadius: 8, border: '1px solid var(--an-border, #2d3748)',
              background: 'var(--an-bg-card, #1a1d2e)', color: 'inherit',
              fontSize: 13, width: 300, outline: 'none',
            }}
          />
          {searchQuery && (
            <span style={{ fontSize: 12, opacity: .6 }}>
              {filtered.length} з {items.filter(i => catFilter === 'all' ? i.category !== 'avoid' : i.category === catFilter).length}
            </span>
          )}
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: .5, fontSize: 16, padding: '0 4px' }}>
              ✕
            </button>
          )}
        </div>

        {(isNoveltyTab ? novelty.loading : intel.loading) && <Loader />}
        {(isNoveltyTab ? novelty.error  : intel.error)  && <ErrBox />}
        {!(isNoveltyTab ? novelty.loading || novelty.error : intel.loading || intel.error) && (
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>#</th>
                  {!isNoveltyTab && <th>Статус</th>}
                  <th colSpan={2}>Товар</th>
                  <th title="Продажи за 7 дней">7д</th>
                  <th title="Продажи за 30 дней">30д</th>
                  <th title="Тренд vs предыдущие 7 дней">Тренд</th>
                  <th title="Средних продаж в день за 30 дней (месячный показатель — устойчивее 7д)">Прод/д</th>
                  <th title="% возвратов за 30д">Возвр.</th>
                  <th title="Текущий остаток">Сток</th>
                  <th title="Дней остатка при скорости продаж за 30д">Дней</th>
                  {!isNoveltyTab && <th title={`Через сколько дней нужно сделать заказ (с учётом ${LEAD_TIME} дней доставки)`}>Заказать</th>}
                  <th title="Средняя цена товара на рынке">Цена</th>
                  <th title="Индекс приоритета закупки (учитывает скорость продаж, срочность, маржинальность)">Score</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.art}
                    className={
                      isNoveltyTab ? (r.has_sales ? '' : 'an-row-muted') :
                      r.category === 'urgent' ? 'an-row-danger' :
                      r.category === 'buy_now' ? 'an-row-warn' :
                      r.category === 'avoid' ? 'an-row-avoid' : ''
                    }
                  >
                    <td className="an-rank">{i + 1}</td>
                    {!isNoveltyTab && (
                      <td>
                        <span className={`an-cat-badge ${CAT_CONFIG[r.category]?.cls || ''}`} title={CAT_CONFIG[r.category]?.tip}>
                          {CAT_CONFIG[r.category]?.label || r.category}
                        </span>
                        {r.is_new && r.category !== 'novelty' && (
                          <span className="an-cat-badge cat-novelty" title="Новий товар — перший продаж за останні 21 день" style={{ marginLeft: 4 }}>🆕</span>
                        )}
                      </td>
                    )}
                    <td colSpan={2}>
                      <ProductCell photo={r.photo} art={r.art} name={r.name}
                        onClick={onReport ? () => onReport(r.art) : undefined} />
                      {!isNoveltyTab && <CheeseStatusBadge statuses={cheeseStatus[r.art]} />}
                    </td>
                    <td className="an-num">
                      {r.v7 > 0 ? <b>{fmt(r.v7)}</b> : <span className="an-muted">—</span>}
                      {r.is_anomaly && (
                        <span className="an-anomaly-flag" title={`Возможная ошибка в учёте: сырое значение ${fmt(r.v7_raw)} (скорректировано по среднему за 30д)`}>⚠️</span>
                      )}
                    </td>
                    <td className="an-num an-muted">{r.v30 > 0 ? fmt(r.v30) : '—'}</td>
                    <td>
                      {r.v7 > 0 || r.v30 > 0 ? <TrendArrow pct={r.trend_pct} /> : <span className="an-muted">—</span>}
                      {r.price_promo_likely && (
                        <span className="an-promo-flag" title={`Резкий рост продаж +${r.trend_pct}%`}>⚡</span>
                      )}
                    </td>
                    <td className="an-num" title="Среднее за 30 дней">{r.daily_avg > 0 ? r.daily_avg : '—'}</td>
                    <td className={`an-num an-pct${r.return_rate > 35 ? ' an-bad' : r.return_rate > 20 ? ' an-warn' : r.return_rate > 0 ? ' an-ok' : ''}`}>
                      {r.return_rate > 0 ? pct(r.return_rate) : '—'}
                    </td>
                    <td className="an-num">{r.stock > 0 ? fmt(r.stock) : <span className="an-muted">0</span>}</td>
                    <td className={`an-days${r.days_left <= LEAD_TIME * 0.5 ? ' an-bad' : r.days_left <= LEAD_TIME ? ' an-warn' : ''}`}
                        title={`Сток на ${r.days_left >= 999 ? '∞' : r.days_left} дней`}>
                      {r.v30 > 0 ? (r.days_left >= 999 ? '∞' : `${r.days_left}д`) : '—'}
                    </td>
                    {!isNoveltyTab && (
                      <td className={`an-order-in${r.order_in === 0 ? ' an-bad' : r.order_in <= 14 ? ' an-warn' : ''}`}
                          title={`До заказа ${r.order_in} дней (доставка ${LEAD_TIME}д)`}>
                        {r.days_left >= 999 ? '∞'
                          : r.order_in === 0 ? '⚠ Сейчас'
                          : `${r.order_in}д`}
                      </td>
                    )}
                    <td className="an-num an-price-cell" title={`Средняя цена: ${r.avg_price}грн • Маржинальность ×${r.margin_f}`}>
                      {r.avg_price > 0 ? `${r.avg_price}` : '—'}
                    </td>
                    <td className="an-score">
                      {r.score}
                      {r.is_off_season && <span className="an-season-badge" title={`Не сезон — индекс ${r.season_idx}`}>🌙</span>}
                    </td>
                    <td className="an-actions">
                      <button className="an-forecast-btn" onClick={() => onForecast(r.art)} title="Прогноз продаж">📈</button>
                      <button className="an-size-btn" onClick={() => setSizesArt({ art: r.art, name: r.name })} title="Размерная сетка">📐</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Evergreens */}
      <Section title="✅ Вечные хиты — всегда продаются, можно везти смело" stale={evergreens.stale}>
        {evergreens.loading && <Loader />}
        {evergreens.error   && <ErrBox />}
        {evergreens.data && (
          <div className="an-table-wrap">
            <table className="an-table">
              <thead>
                <tr>
                  <th>#</th><th colSpan={2}>Товар</th>
                  <th title="Дней продаж из последних 30">Стабильность</th>
                  <th>Прод/день</th><th>Возвраты</th>
                  <th>Сток</th><th>Дней</th><th />
                </tr>
              </thead>
              <tbody>
                {evergreens.data.map((r, i) => (
                  <tr key={r.art}>
                    <td className="an-rank">{i + 1}</td>
                    <td colSpan={2}>
                      <ProductCell photo={r.photo} art={r.art} name={r.name}
                        onClick={onReport ? () => onReport(r.art) : undefined} />
                    </td>
                    <td>
                      <div className="an-stability">
                        <div className="an-stability-bar" style={{ width: `${Math.min(r.days_sold / 30 * 100, 100)}%` }} />
                        <span>{r.days_sold}/30д</span>
                      </div>
                    </td>
                    <td className="an-num">{r.daily_avg}</td>
                    <td className={`an-num an-pct${r.return_rate > 25 ? ' an-warn' : ' an-ok'}`}>
                      {pct(r.return_rate)}
                    </td>
                    <td className="an-num">{fmt(r.stock)}</td>
                    <td className={`an-days${r.days_left <= 7 ? ' an-bad' : r.days_left <= 14 ? ' an-warn' : ''}`}>
                      {r.days_left >= 999 ? '∞' : `${r.days_left}д`}
                    </td>
                    <td className="an-actions">
                      <button className="an-forecast-btn" onClick={() => onForecast(r.art)} title="Прогноз">📈</button>
                      <button className="an-size-btn" onClick={() => setSizesArt({ art: r.art, name: r.name })} title="Размеры">📐</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cheese purchase analysis
// ---------------------------------------------------------------------------

const DIAG_LABELS = {
  price_too_high: { label: 'Высокая цена', color: '#e53935' },
  off_season:     { label: 'Не сезон',     color: '#1976d2' },
  slow_velocity:  { label: 'Медленно',     color: '#f57c00' },
  overstock:      { label: 'Излишек',      color: '#fbc02d' },
  ok:             { label: 'Норма',        color: '#43a047' },
};
const ACTION_LABELS = {
  reduce_price: 'Снизить цену',
  wait_season:  'Ждать сезон',
  liquidate:    'Ликвидировать',
  monitor:      'Следить',
  ok:           '—',
};

function SellThroughBar({ value }) {
  const pct = Math.min(Math.round((value || 0) * 100), 100);
  const color = pct >= 50 ? '#43a047' : pct >= 25 ? '#fbc02d' : '#e53935';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--bg-secondary,#eee)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

function CheeseAnalysisTab({ onReport }) {
  const [minDays,   setMinDays]   = useState(30);
  const [threshold, setThreshold] = useState(0.25);
  const [showAll,   setShowAll]   = useState(false);

  const { data, loading, error } = useCached(
    () => analyticsAPI.getCheesePurchaseAnalysis({ min_days: minDays, threshold }),
    `cheese_analysis:${minDays}:${threshold}`,
  );

  const problems  = data?.problems  || [];
  const allItems  = data?.all_items || [];
  const patterns  = data?.patterns  || {};
  const summary   = patterns.summary || {};
  const displayRows = showAll ? allItems : problems;

  const fmtUah = v => v >= 1000 ? `${Math.round(v / 1000)}к ₴` : `${v} ₴`;

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          Мин. дней с получения:
          <select value={minDays} onChange={e => setMinDays(+e.target.value)} className="an-select">
            {[14,21,30,45,60].map(d => <option key={d} value={d}>{d} дн.</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          Порог продаваемости:
          <select value={threshold} onChange={e => setThreshold(+e.target.value)} className="an-select">
            {[0.10, 0.15, 0.20, 0.25, 0.33, 0.50].map(t => (
              <option key={t} value={t}>{Math.round(t * 100)}%</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
          Показать все ({allItems.length})
        </label>
      </div>

      {loading && <div className="an-loading">Загрузка…</div>}
      {error   && <div className="an-error">Ошибка загрузки</div>}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Проблемных позиций', value: summary.problem_items ?? 0, color: '#e53935' },
              { label: 'Зависло пар',        value: summary.total_stuck_pairs ?? 0, color: '#f57c00' },
              { label: 'Заморожено',         value: summary.total_stuck_uah ? fmtUah(summary.total_stuck_uah) : '0 ₴', color: '#c62828' },
              { label: 'Средняя продаваемость', value: `${Math.round((summary.avg_sell_through ?? 0) * 100)}%`, color: '#1565c0' },
            ].map(card => (
              <div key={card.label} style={{
                background: 'var(--bg-secondary,#f5f5f5)', borderRadius: 10, padding: '12px 18px',
                minWidth: 140, flex: '1 1 140px',
                borderLeft: `4px solid ${card.color}`,
              }}>
                <div style={{ fontSize: 11, opacity: .65, marginBottom: 4 }}>{card.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
              </div>
            ))}
          </div>

          {/* Problem / All items table */}
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>
              {showAll ? `Все закупки (${allItems.length})` : `Проблемные закупки (${problems.length})`}
            </h3>
          </div>

          {displayRows.length === 0
            ? <div className="an-empty">{showAll ? 'Нет полученных закупок' : 'Проблемных закупок нет — всё хорошо!'}</div>
            : (
              <div className="an-table-wrap" style={{ maxHeight: 520, overflowY: 'auto', marginBottom: 28 }}>
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>Товар</th>
                      <th>Категория</th>
                      <th>Получено</th>
                      <th title="Куплено / Продано пар">Куп./Прод.</th>
                      <th>Продаваемость</th>
                      <th>Зависло</th>
                      <th>Зависло ₴</th>
                      <th>Диагноз</th>
                      <th>Действие</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map(r => {
                      const diag = DIAG_LABELS[r.diagnosis] || DIAG_LABELS.ok;
                      return (
                        <tr key={r.id}
                          className={onReport ? 'an-sales-tile--clickable' : ''}
                          onClick={onReport && r.catalog_sku ? () => onReport(r.catalog_sku) : undefined}
                        >
                          <td>
                            <div className="an-product-row">
                              {r.photo_url
                                ? <img className="an-thumb" src={proxyImageUrl(r.photo_url)} alt="" />
                                : <div className="an-thumb an-thumb--empty" />
                              }
                              <div className="an-product-info">
                                <span className="an-art-cell">{r.catalog_sku}</span>
                                <span className="an-name-cell">{r.name}</span>
                              </div>
                            </div>
                          </td>
                          <td style={{ fontSize: 12 }}>{r.category_name}</td>
                          <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {r.received_at}<br />
                            <span style={{ opacity: .55 }}>{r.days_since_received} дн. назад</span>
                          </td>
                          <td style={{ textAlign: 'center', fontSize: 13 }}>
                            {r.total_purchased}<br />
                            <span style={{ color: '#43a047', fontWeight: 600 }}>{r.total_sold}</span>
                          </td>
                          <td><SellThroughBar value={r.sell_through} /></td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: r.stuck_pairs > 0 ? '#e53935' : undefined }}>
                            {r.stuck_pairs}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: r.stuck_uah > 0 ? '#c62828' : undefined }}>
                            {r.stuck_uah > 0 ? fmtUah(r.stuck_uah) : '—'}
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
                              background: diag.color + '22', color: diag.color, fontWeight: 600,
                            }}>{diag.label}</span>
                          </td>
                          <td style={{ fontSize: 12, opacity: .8 }}>{ACTION_LABELS[r.action] || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }

          {/* Level 3: Patterns */}
          {(patterns.by_category?.length > 0 || patterns.by_supplier?.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 8 }}>

              {/* By category */}
              <div>
                <h4 style={{ marginBottom: 8, fontSize: 14 }}>По категориям</h4>
                <div className="an-table-wrap">
                  <table className="an-table">
                    <thead>
                      <tr>
                        <th>Категория</th>
                        <th>Позиций</th>
                        <th>Зависло</th>
                        <th>₴</th>
                        <th>Продав.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(patterns.by_category || []).map(r => (
                        <tr key={r.name}>
                          <td style={{ fontSize: 12 }}>{r.name}</td>
                          <td style={{ textAlign: 'center' }}>{r.items}</td>
                          <td style={{ textAlign: 'center', color: r.stuck_pairs > 0 ? '#e53935' : undefined }}>
                            {r.stuck_pairs}
                          </td>
                          <td style={{ textAlign: 'right', color: r.stuck_uah > 0 ? '#c62828' : undefined }}>
                            {r.stuck_uah > 0 ? fmtUah(r.stuck_uah) : '—'}
                          </td>
                          <td><SellThroughBar value={r.avg_sell_through} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* By market supplier — кто ещё продаёт те же модели */}
              <div>
                <h4 style={{ marginBottom: 4, fontSize: 14 }}>По поставщикам на рынке</h4>
                <p style={{ fontSize: 11, opacity: .55, marginBottom: 8, marginTop: 0 }}>
                  Кто ещё продаёт наши модели и сколько — за последние 90 дней
                </p>
                <div className="an-table-wrap">
                  <table className="an-table">
                    <thead>
                      <tr>
                        <th>Поставщик</th>
                        <th title="Общих моделей с нашими закупками">Моделей</th>
                        <th title="Сколько они продали пар за 90 дней">Их прод.</th>
                        <th title="Сколько мы продали тех же моделей">Наши прод.</th>
                        <th title="Их продажи / наши продажи. Больше 1 — они продают лучше нас">Разрыв ×</th>
                        <th>Наша прод.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(patterns.by_supplier || []).map(r => {
                        const ratioColor = r.ratio >= 3 ? '#e53935' : r.ratio >= 1.5 ? '#f57c00' : '#43a047';
                        return (
                          <tr key={r.name}>
                            <td style={{ fontSize: 12 }}>{r.name}</td>
                            <td style={{ textAlign: 'center' }}>{r.items}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.their_sales}</td>
                            <td style={{ textAlign: 'center' }}>{r.our_sold}</td>
                            <td style={{ textAlign: 'center', fontWeight: 700, color: ratioColor }}>
                              {r.ratio}×
                            </td>
                            <td><SellThroughBar value={r.our_sell_through} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'overview',   label: 'Обзор' },
  { key: 'products',   label: 'Товары' },
  { key: 'suppliers',  label: 'Поставщики' },
  { key: 'restock',    label: 'Закупки' },
  { key: 'lost',       label: '👻 Упущенные' },
  { key: 'cheese_analysis', label: '🧀 Анализ закупок' },
];

export default function AnalyticsTab() {
  const [tab,                  setTab]                = useState('overview');
  const [forecastArt,          setForecast]           = useState(null);
  const [reportArt,            setReportArt]          = useState(null);
  const [productSearch,        setProductSearch]      = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const searchTimerRef = useRef(null);

  // Debounced product search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!productSearch.trim()) { setProductSearchResults([]); return; }
    searchTimerRef.current = setTimeout(() => {
      analyticsAPI.searchProducts(productSearch)
        .then(r => setProductSearchResults(r.data || []))
        .catch(() => setProductSearchResults([]));
    }, 400);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [productSearch]);

  function handleSearchSelect(art) {
    setProductSearch('');
    setProductSearchResults([]);
    setReportArt(art);
  }

  return (
    <div className="an-root">

      {/* Product search */}
      <div className="an-product-search-wrap">
        <input
          className="an-product-search-input"
          placeholder="Найти товар по артикулу или названию…"
          value={productSearch}
          onChange={e => setProductSearch(e.target.value)}
          onBlur={() => setTimeout(() => setProductSearchResults([]), 200)}
        />
        {productSearchResults.length > 0 && (
          <div className="an-product-search-dropdown">
            {productSearchResults.map(r => (
              <div key={r.art} className="an-product-search-item" onMouseDown={() => handleSearchSelect(r.art)}>
                {r.photo
                  ? <img className="an-product-search-thumb" src={proxyImageUrl(r.photo)} alt="" loading="lazy" />
                  : <div className="an-product-search-thumb" />
                }
                <div className="an-product-search-info">
                  <span className="an-product-search-art">{r.art}</span>
                  <span className="an-product-search-name">{r.name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="an-tabs">
        {TABS.map(t => (
          <button key={t.key}
            className={`an-tab-btn${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'overview'  && <OverviewTab />}
      {tab === 'products'  && <ProductsTab  onForecast={setForecast} onReport={setReportArt} />}
      {tab === 'suppliers' && <SuppliersTab onReport={setReportArt} />}
      {tab === 'restock'   && <RestockTab   onForecast={setForecast} onReport={setReportArt} />}
      {tab === 'lost'           && <LostTab          onReport={setReportArt} />}
      {tab === 'cheese_analysis' && <CheeseAnalysisTab onReport={setReportArt} />}

      {/* Modals last — поверх всего остального */}
      {forecastArt && (
        <ForecastModal art={forecastArt} onClose={() => setForecast(null)} />
      )}
      {reportArt && (
        <ProductReportModal art={reportArt} onClose={() => setReportArt(null)} />
      )}
    </div>
  );
}
